<template>
  <div class="product-performance-dashboard">
    <!-- ===== 3D CUBE LOADER ===== -->
    <div v-if="isLoading" class="global-loader">
      <div class="loader-3d">
        <div class="cube">
          <div class="face front"></div>
          <div class="face back"></div>
          <div class="face right"></div>
          <div class="face left"></div>
          <div class="face top"></div>
          <div class="face bottom"></div>
        </div>
      </div>
      <p class="loader-text">Loading Product Performance Data...</p>
    </div>

    <!-- ===== HEADER ===== -->
    <div class="dashboard-header" v-if="!isLoading">
      <div class="header-content">
        <div class="header-left">
          <div class="header-icon">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="2" y="2" width="20" height="20" rx="2.18"/>
              <line x1="8" y1="2" x2="8" y2="22"/>
              <line x1="16" y1="2" x2="16" y2="22"/>
              <line x1="2" y1="8" x2="22" y2="8"/>
              <line x1="2" y1="16" x2="22" y2="16"/>
            </svg>
          </div>
          <div>
            <h1 class="header-title">Product Performance</h1>
            <p class="header-subtitle">Real-time product analytics &amp; performance metrics</p>
          </div>
        </div>
        <div class="header-right">
          <div class="header-badge">
            <span class="badge-dot"></span>
            <span>Live</span>
          </div>
          <div class="header-user">
            <b-icon-person-circle size="1.5rem" />
            <span>{{ getUserName() }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== FILTER BAR ===== -->
    <div class="filter-bar" v-if="!isLoading">
      <div class="filter-content">
        <div class="filter-left">
          <i class="fas fa-calendar-alt text-primary mr-2"></i>
          <span class="filter-label">Date Range:</span>
          <date-picker
            v-model="dateRange"
            type="date"
            range
            placeholder="Select date range"
            value-type="YYYY-MM-DD"
            format="MMM DD, YYYY"
            class="date-picker"
            style="width: 260px;"
            @input="onDateChange"
          />
          <b-form-select
            v-model="selectedState"
            :options="stateOptions"
            size="sm"
            class="ml-2"
            style="width: 140px;"
            @change="onFilterChange"
          />
          <b-form-select
            v-model="pickedLocation"
            :options="locationOptions"
            size="sm"
            class="ml-2"
            style="width: 160px;"
            @change="onFilterChange"
          >
            <template #first>
              <b-form-select-option :value="null">All Locations</b-form-select-option>
            </template>
          </b-form-select>
          <b-form-select
            v-model="pickedUser"
            :options="userOptions"
            size="sm"
            class="ml-2"
            style="width: 160px;"
            @change="onFilterChange"
          >
            <template #first>
              <b-form-select-option :value="null">All Salespeople</b-form-select-option>
            </template>
          </b-form-select>
        </div>
        <div class="filter-right">
          <b-button variant="outline-secondary" size="sm" @click="clearFilters" class="mr-2">
            <i class="fas fa-times"></i> Clear
          </b-button>
          <b-button variant="primary" size="sm" @click="refreshData" :disabled="isLoading || isFetching">
            <i class="fas fa-sync-alt mr-1" :class="{ 'fa-spin': isFetching }"></i> Refresh
          </b-button>
        </div>
      </div>
    </div>

    <!-- ===== MAIN TABS ===== -->
    <b-tabs v-model="activeMainTab" pills card class="main-tabs" v-if="!isLoading">
      <!-- TAB 1: OVERVIEW -->
      <b-tab title="📊 Overview" active>
        <!-- 6 FLIP CARDS -->
        <div class="period-grid" v-if="periodCards && periodCards.length">
          <div 
            v-for="period in periodCards" 
            :key="period.key"
            class="flip-card"
            :class="{ active: activePeriod === period.key, flipped: period.flipped }"
          >
            <div class="flip-card-inner">
              <!-- FRONT SIDE -->
              <div class="flip-card-front" @click="openPeriodModal(period.key)">
                <div class="flip-card-header">
                  <span class="flip-label">{{ period.label }}</span>
                </div>
                
                <!-- <div class="flip-change-indicator" v-if="period.change !== null">
                  <span class="change-badge" :class="period.changeDirection">
                    <i :class="period.changeDirection === 'up' ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"></i>
                    {{ Math.abs(period.change).toFixed(1) }}%
                  </span>
                  <span class="change-label">vs {{ getPreviousPeriodLabel(period.key) }}</span>
                </div> -->
                
                <div class="flip-main-stats">
                  <div class="flip-stat">
                    <span class="flip-stat-label">Total Products</span><br>
                    <span class="flip-stat-value primary">{{ formatNumber(period.totalProducts || 0) }}</span>
                  </div>
                  <div class="flip-stat">
                    <span class="flip-stat-label">Top Product</span>
                    <span class="flip-stat-value text-truncate" style="font-size: 16px;" :title="period.topProduct">
                      {{ truncateText(period.topProduct, 18) || 'N/A' }}
                    </span>
                  </div>
                </div>
                
                <div class="flip-detail-grid">
                  <div class="flip-detail-card">
                    <span class="flip-detail-label">Revenue</span>
                    <span class="flip-detail-value">{{ formatCurrency(period.revenue || 0) }}</span>
                  </div>
                  <div class="flip-detail-card">
                    <span class="flip-detail-label">Units Sold</span>
                    <span class="flip-detail-value">{{ formatNumber(period.unitsSold || 0) }}</span>
                  </div>
                  <div class="flip-detail-card">
                    <span class="flip-detail-label">Orders</span>
                    <span class="flip-detail-value">{{ formatNumber(period.orders || 0) }}</span>
                  </div>
                  <div class="flip-detail-card">
                    <span class="flip-detail-label">In Stock</span>
                    <span class="flip-detail-value">{{ formatNumber(period.inStock || 0) }}</span>
                  </div>
                  <div class="flip-detail-card">
                    <span class="flip-detail-label">Avg Price</span>
                    <span class="flip-detail-value">{{ formatCurrency(period.avgPrice || 0) }}</span>
                  </div>
                  <div class="flip-detail-card">
                    <span class="flip-detail-label">Profit %</span>
                    <span class="flip-detail-value" :class="getProfitClass(period.profitMargin || 0)">{{ formatNumber(period.profitMargin || 0) }}%</span>
                  </div>
                </div>
                
                <div class="flip-footer">
                  <span class="flip-orders">📦 {{ (period.categories || []).length }} categories</span>
                  <div class="flip-actions">
                    <span class="flip-hint" @click.stop="toggleFlip(period.key)">
                      <b-icon-arrow-repeat class="flip-hint-icon" />
                      Flip for charts
                    </span>
                  </div>
                </div>
              </div>
              
              <!-- BACK SIDE -->
              <div class="flip-card-back" @click="openPeriodModal(period.key)">
                <div class="flip-back-header">
                  <span class="flip-back-label">{{ period.label }} - Charts</span>
                  <span class="flip-hint back" @click.stop="toggleFlip(period.key)">
                    <b-icon-arrow-repeat class="flip-hint-icon" />
                    Flip back
                  </span>
                </div>
                
                <div class="flip-charts-layout">
                  <div class="flip-chart-section">
                    <div class="flip-chart-title">Revenue by Category</div>
                    <div class="flip-chart-wrapper">
                      <canvas :id="'category_pie_' + period.key" class="flip-chart"></canvas>
                    </div>
                    <div class="flip-chart-stats">
                      <div v-for="(cat, idx) in (period.categories || []).slice(0, 3)" :key="idx" class="stats-label-item">
                        <span class="stats-dot" :style="{ background: getCategoryColor(idx) }"></span>
                        <span class="stats-label-text">{{ cat.category_name || 'Unknown' }}</span>
                        <span class="stats-label-value">{{ formatCurrency(cat.total_revenue || 0) }}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div class="flip-chart-section">
                    <div class="flip-chart-title">Top Products</div>
                    <div class="flip-chart-wrapper">
                      <canvas :id="'product_pie_' + period.key" class="flip-chart"></canvas>
                    </div>
                    <div class="flip-chart-stats payment-stats">
                      <div v-for="(p, idx) in (period.topProducts || []).slice(0, 4)" :key="idx" class="stats-label-item">
                        <span class="stats-dot" :style="{ background: getProductColor(idx) }"></span>
                        <span class="stats-label-text text-truncate" style="max-width: 60px;" :title="p.name">
                          {{ truncateText(p.name, 12) || 'Unknown' }}
                        </span>
                        <span class="stats-label-value">{{ formatNumber(p.units || 0) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="flip-back-footer">
                  <span class="flip-orders">📦 {{ period.totalProducts || 0 }} total products</span>
                  <span class="flip-payment-count">{{ (period.categories || []).length }} categories</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- CATEGORY & PRODUCT CHARTS -->
        <div class="charts-row" v-if="hasChartData">
          <div class="chart-card">
            <div class="chart-header">
              <h6><i class="fas fa-folder-open text-primary mr-1"></i> Revenue by Category</h6>
              <b-button variant="link" size="sm" @click="openCategoryList">View All</b-button>
            </div>
            <div class="chart-container">
              <canvas ref="categoryChart"></canvas>
            </div>
          </div>
          <div class="chart-card">
            <div class="chart-header">
              <h6><i class="fas fa-box text-primary mr-1"></i> Top Products by Revenue</h6>
              <b-button variant="link" size="sm" @click="openProductModal(null)">View All</b-button>
            </div>
            <div class="chart-container">
              <canvas ref="productChart"></canvas>
            </div>
          </div>
        </div>

   
      </b-tab>

      <!-- TAB 2: BEST SELLERS -->
      <b-tab 
        title="🏆 Best Sellers"
        @click="loadBestSellersTab"
      >
        <BestSellersDashboard 
          :userDetails="userDetails"
          :showBestSellersDashboard="true"
          ref="bestSellersDashboard"
        />
      </b-tab>

      <!-- TAB 3: ORDER TYPES -->
      <b-tab 
        title="📦 Order Types"
        @click="loadOrderTypesTab"
      >
        <div v-if="tabLoading.orderTypes" class="tab-loader">
          <div class="loader-3d small">
            <div class="cube">
              <div class="face front"></div>
              <div class="face back"></div>
              <div class="face right"></div>
              <div class="face left"></div>
              <div class="face top"></div>
              <div class="face bottom"></div>
            </div>
          </div>
          <p class="loader-text">Loading order type data...</p>
        </div>
        
        <div v-else-if="tabsLoaded.orderTypes" class="order-types-dashboard">
          <!-- Order Type Summary Stats -->
          <div class="order-type-stats-row">
            <div class="stats-card">
              <div class="stats-card-icon order-types"><i class="fas fa-shopping-cart"></i></div>
              <div class="stats-card-content">
                <span class="stats-card-value">{{ formatNumber(orderTypeData.totalOrders || 0) }}</span>
                <span class="stats-card-label">Total Orders</span>
              </div>
            </div>
            <div class="stats-card">
              <div class="stats-card-icon store-pickup"><i class="fas fa-store"></i></div>
              <div class="stats-card-content">
                <span class="stats-card-value">{{ formatNumber(orderTypeData.storePickup || 0) }}</span>
                <span class="stats-card-label">Store Pickup</span>
              </div>
            </div>
            <div class="stats-card">
              <div class="stats-card-icon home-delivery"><i class="fas fa-truck"></i></div>
              <div class="stats-card-content">
                <span class="stats-card-value">{{ formatNumber(orderTypeData.homeDelivery || 0) }}</span>
                <span class="stats-card-label">Home Delivery</span>
              </div>
            </div>
            <div class="stats-card">
              <div class="stats-card-icon layaway"><i class="fas fa-clock"></i></div>
              <div class="stats-card-content">
                <span class="stats-card-value">{{ formatNumber(orderTypeData.layaway || 0) }}</span>
                <span class="stats-card-label">Layaway</span>
              </div>
            </div>
            <div class="stats-card">
              <div class="stats-card-icon regular"><i class="fas fa-receipt"></i></div>
              <div class="stats-card-content">
                <span class="stats-card-value">{{ formatNumber(orderTypeData.regular || 0) }}</span>
                <span class="stats-card-label">Regular Sales</span>
              </div>
            </div>
          </div>

          <!-- Order Type Charts -->
          <div class="charts-row">
            <div class="chart-card">
              <div class="chart-header">
                <h6><i class="fas fa-chart-pie text-primary mr-1"></i> Orders by Type</h6>
              </div>
              <div class="chart-container" style="height: 250px;">
                <canvas ref="orderTypeChart"></canvas>
              </div>
            </div>
            <div class="chart-card">
              <div class="chart-header">
                <h6><i class="fas fa-chart-bar text-primary mr-1"></i> Units by Order Type</h6>
              </div>
              <div class="chart-container" style="height: 250px;">
                <canvas ref="orderTypeUnitsChart"></canvas>
              </div>
            </div>
          </div>

          <!-- Order Type Detailed Table -->
          <div class="chart-card mt-3">
            <div class="chart-header">
              <h6><i class="fas fa-table text-primary mr-1"></i> Order Type Breakdown</h6>
            </div>
            <div class="table-responsive-wrapper" style="max-height: 400px;">
              <table class="modal-table">
                <thead>
                  <tr>
                    <th>Order Type</th>
                    <th class="text-right">Orders</th>
                    <th class="text-right">Units Sold</th>
                    <th class="text-right">Revenue</th>
                    <th class="text-right">Avg Order Value</th>
                    <th class="text-right">% of Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="type in orderTypeData.breakdown || []" :key="type.order_type">
                    <td><strong>{{ type.order_type || 'Unknown' }}</strong></td>
                    <td class="text-right">{{ formatNumber(type.order_count || 0) }}</td>
                    <td class="text-right">{{ formatNumber(type.units_sold || 0) }}</td>
                    <td class="text-right font-weight-bold text-primary">{{ formatCurrency(type.revenue || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(type.avg_order_value || 0) }}</td>
                    <td class="text-right">{{ type.percentage || 0 }}%</td>
                  </tr>
                  <tr v-if="!orderTypeData.breakdown || !orderTypeData.breakdown.length">
                    <td colspan="6" class="text-center text-muted">No order type data available</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </b-tab>

      <!-- TAB 4: VENDORS -->
      <b-tab 
        title="🏭 Vendors"
        @click="loadVendorsTab"
      >
        <div v-if="tabLoading.vendors" class="tab-loader">
          <div class="loader-3d small">
            <div class="cube">
              <div class="face front"></div>
              <div class="face back"></div>
              <div class="face right"></div>
              <div class="face left"></div>
              <div class="face top"></div>
              <div class="face bottom"></div>
            </div>
          </div>
          <p class="loader-text">Loading vendor data...</p>
        </div>
        
        <div v-else-if="tabsLoaded.vendors" class="vendors-dashboard">
          <!-- Vendor Summary Stats -->
          <div class="vendor-stats-row">
            <div class="stats-card">
              <div class="stats-card-icon total-vendors"><i class="fas fa-building"></i></div>
              <div class="stats-card-content">
                <span class="stats-card-value">{{ formatNumber(vendorData.totalVendors || 0) }}</span>
                <span class="stats-card-label">Total Vendors</span>
              </div>
            </div>
            <div class="stats-card">
              <div class="stats-card-icon total-sales"><i class="fas fa-dollar-sign"></i></div>
              <div class="stats-card-content">
                <span class="stats-card-value">{{ formatCurrency(vendorData.totalRevenue || 0) }}</span>
                <span class="stats-card-label">Total Revenue</span>
              </div>
            </div>
            <div class="stats-card">
              <div class="stats-card-icon total-returns"><i class="fas fa-undo"></i></div>
              <div class="stats-card-content">
                <span class="stats-card-value">{{ formatNumber(vendorData.totalReturns || 0) }}</span>
                <span class="stats-card-label">Total Returns</span>
              </div>
            </div>
            <div class="stats-card">
              <div class="stats-card-icon top-vendor"><i class="fas fa-trophy"></i></div>
              <div class="stats-card-content">
                <span class="stats-card-value text-truncate" style="font-size: 16px;" :title="vendorData.topVendor">
                  {{ truncateText(vendorData.topVendor, 18) || 'N/A' }}
                </span>
                <span class="stats-card-label">Top Vendor</span>
              </div>
            </div>
          </div>

          <!-- Vendor Charts -->
          <div class="charts-row">
            <div class="chart-card">
              <div class="chart-header">
                <h6><i class="fas fa-chart-pie text-primary mr-1"></i> Revenue by Vendor</h6>
              </div>
              <div class="chart-container" style="height: 250px;">
                <canvas ref="vendorRevenueChart"></canvas>
              </div>
            </div>
            <div class="chart-card">
              <div class="chart-header">
                <h6><i class="fas fa-chart-bar text-primary mr-1"></i> Returns by Vendor</h6>
              </div>
              <div class="chart-container" style="height: 250px;">
                <canvas ref="vendorReturnsChart"></canvas>
              </div>
            </div>
          </div>

          <!-- Vendor Detailed Table -->
          <div class="chart-card mt-3">
            <div class="chart-header">
              <h6><i class="fas fa-table text-primary mr-1"></i> Vendor Performance</h6>
            </div>
            <div class="table-responsive-wrapper" style="max-height: 400px;">
              <table class="modal-table">
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th class="text-right">Products</th>
                    <th class="text-right">Units Sold</th>
                    <th class="text-right">Revenue</th>
                    <th class="text-right">Returns</th>
                    <th class="text-right">Return Rate</th>
                    <th class="text-right">Avg Price</th>
                    <th class="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="vendor in vendorData.breakdown || []" :key="vendor.vendor">
                    <td><strong>{{ vendor.vendor || 'Unknown' }}</strong></td>
                    <td class="text-right">{{ formatNumber(vendor.product_count || 0) }}</td>
                    <td class="text-right">{{ formatNumber(vendor.units_sold || 0) }}</td>
                    <td class="text-right font-weight-bold text-primary">{{ formatCurrency(vendor.revenue || 0) }}</td>
                    <td class="text-right"><span :class="getReturnRateClass(vendor.return_rate || 0)">{{ formatNumber(vendor.returns || 0) }}</span></td>
                    <td class="text-right"><span :class="getReturnRateClass(vendor.return_rate || 0)">{{ vendor.return_rate || 0 }}%</span></td>
                    <td class="text-right">{{ formatCurrency(vendor.avg_price || 0) }}</td>
                    <td class="text-center">
                      <b-button variant="outline-primary" size="sm" @click="openVendorDetail(vendor)">
                        <i class="fas fa-eye"></i>
                      </b-button>
                    </td>
                  </tr>
                  <tr v-if="!vendorData.breakdown || !vendorData.breakdown.length">
                    <td colspan="8" class="text-center text-muted">No vendor data available</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </b-tab>

      <!-- TAB 5: STATE ANALYSIS -->
      <b-tab 
        title="🔄 State Analysis"
        @click="loadStateAnalysisTab"
      >
        <div v-if="tabLoading.stateAnalysis" class="tab-loader">
          <div class="loader-3d small">
            <div class="cube">
              <div class="face front"></div>
              <div class="face back"></div>
              <div class="face right"></div>
              <div class="face left"></div>
              <div class="face top"></div>
              <div class="face bottom"></div>
            </div>
          </div>
          <p class="loader-text">Loading state analysis data...</p>
        </div>
        
        <div v-else-if="tabsLoaded.stateAnalysis" class="state-analysis-dashboard">
          <!-- State Summary Stats -->
          <div class="state-stats-row">
            <div class="stats-card">
              <div class="stats-card-icon total-items"><i class="fas fa-boxes"></i></div>
              <div class="stats-card-content">
                <span class="stats-card-value">{{ formatNumber(stateData.totalItems || 0) }}</span>
                <span class="stats-card-label">Total Items</span>
              </div>
            </div>
            <div class="stats-card">
              <div class="stats-card-icon delivered"><i class="fas fa-check-circle"></i></div>
              <div class="stats-card-content">
                <span class="stats-card-value">{{ formatNumber(stateData.delivered || 0) }}</span>
                <span class="stats-card-label">Delivered</span>
              </div>
            </div>
            <div class="stats-card">
              <div class="stats-card-icon returned"><i class="fas fa-undo"></i></div>
              <div class="stats-card-content">
                <span class="stats-card-value">{{ formatNumber(stateData.returned || 0) }}</span>
                <span class="stats-card-label">Returned</span>
              </div>
            </div>
            <div class="stats-card">
              <div class="stats-card-icon pending"><i class="fas fa-clock"></i></div>
              <div class="stats-card-content">
                <span class="stats-card-value">{{ formatNumber(stateData.pending || 0) }}</span>
                <span class="stats-card-label">Pending</span>
              </div>
            </div>
            <div class="stats-card">
              <div class="stats-card-icon cancelled"><i class="fas fa-times-circle"></i></div>
              <div class="stats-card-content">
                <span class="stats-card-value">{{ formatNumber(stateData.cancelled || 0) }}</span>
                <span class="stats-card-label">Cancelled</span>
              </div>
            </div>
          </div>

          <!-- State Charts -->
          <div class="charts-row">
            <div class="chart-card">
              <div class="chart-header">
                <h6><i class="fas fa-chart-pie text-primary mr-1"></i> Items by State</h6>
              </div>
              <div class="chart-container" style="height: 250px;">
                <canvas ref="statePieChart"></canvas>
              </div>
            </div>
            <div class="chart-card">
              <div class="chart-header">
                <h6><i class="fas fa-chart-bar text-primary mr-1"></i> State Distribution</h6>
              </div>
              <div class="chart-container" style="height: 250px;">
                <canvas ref="stateBarChart"></canvas>
              </div>
            </div>
          </div>

          <!-- State Detailed Table -->
          <div class="chart-card mt-3">
            <div class="chart-header">
              <h6><i class="fas fa-table text-primary mr-1"></i> State Breakdown</h6>
              <div class="state-filter-group">
                <b-form-select
                  v-model="stateViewFilter"
                  :options="stateViewOptions"
                  size="sm"
                  style="width: 150px;"
                  @change="filterStateData"
                />
              </div>
            </div>
            <div class="table-responsive-wrapper" style="max-height: 400px;">
              <table class="modal-table">
                <thead>
                  <tr>
                    <th>State</th>
                    <th class="text-right">Items</th>
                    <th class="text-right">Products</th>
                    <th class="text-right">Revenue</th>
                    <th class="text-right">% of Total</th>
                    <th class="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="state in filteredStateData" :key="state.state_value">
                    <td><strong>{{ state.state_text || 'Unknown' }}</strong></td>
                    <td class="text-right">{{ formatNumber(state.item_count || 0) }}</td>
                    <td class="text-right">{{ formatNumber(state.product_count || 0) }}</td>
                    <td class="text-right font-weight-bold text-primary">{{ formatCurrency(state.revenue || 0) }}</td>
                    <td class="text-right">{{ state.percentage || 0 }}%</td>
                    <td class="text-center">
                      <b-badge :variant="getStateBadgeVariant(state.state_value)">
                        {{ state.state_text || 'Unknown' }}
                      </b-badge>
                    </td>
                  </tr>
                  <tr v-if="!filteredStateData || !filteredStateData.length">
                    <td colspan="6" class="text-center text-muted">No state data available</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </b-tab>
    </b-tabs>

    <!-- ===== CATEGORY LIST MODAL ===== -->
    <b-modal 
      v-model="showCategoryListModal" 
      title="All Categories"
      size="xl"
      hide-footer
      body-class="period-modal-body"
      header-class="period-modal-header"
      @hidden="onModalHidden"
    >
      <div class="modal-summary-grid">
        <div class="modal-summary-item">
          <div class="summary-icon total"><b-icon-box-seam /></div>
          <div>
            <span class="label">Products</span>
            <span class="value">{{ formatNumber(totalProducts) }}</span>
          </div>
        </div>
        <div class="modal-summary-item">
          <div class="summary-icon revenue"><b-icon-cash-stack /></div>
          <div>
            <span class="label">Revenue</span>
            <span class="value">{{ formatCurrency(totalRevenue) }}</span>
          </div>
        </div>
        <div class="modal-summary-item">
          <div class="summary-icon units"><b-icon-boxes /></div>
          <div>
            <span class="label">Units Sold</span>
            <span class="value">{{ formatNumber(totalUnits) }}</span>
          </div>
        </div>
        <div class="modal-summary-item">
          <div class="summary-icon orders"><b-icon-receipt /></div>
          <div>
            <span class="label">Orders</span>
            <span class="value">{{ formatNumber((summaryData && summaryData.total_orders) || 0) }}</span>
          </div>
        </div>
        <div class="modal-summary-item">
          <div class="summary-icon profit"><b-icon-graph-up /></div>
          <div>
            <span class="label">Avg Profit</span>
            <span class="value" :class="getProfitClass(avgProfit)">{{ formatNumber(avgProfit) }}%</span>
          </div>
        </div>
      </div>

      <div class="table-responsive-wrapper" style="max-height: 400px;">
        <table class="modal-table clickable-rows">
          <thead>
            <tr>
              <th>Category</th>
              <th class="text-right">Products</th>
              <th class="text-right">Revenue</th>
              <th class="text-right">Units</th>
              <th class="text-right">Orders</th>
              <th class="text-right">Avg Price</th>
              <th class="text-right">Profit %</th>
              <th class="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="cat in categoryData" :key="cat.category_id" @click="openCategoryDetail(cat)">
              <td><strong>{{ cat.category_name }}</strong></td>
              <td class="text-right">{{ formatNumber(cat.product_count) }}</td>
              <td class="text-right font-weight-bold text-primary">{{ formatCurrency(cat.total_sales) }}</td>
              <td class="text-right">{{ formatNumber(cat.total_units_sold) }}</td>
              <td class="text-right">{{ formatNumber(cat.order_count) }}</td>
              <td class="text-right">{{ formatCurrency(cat.avg_order_value) }}</td>
              <td class="text-right"><span :class="getProfitClass(cat.avg_profit_margin)">{{ formatNumber(cat.avg_profit_margin) }}%</span></td>
              <td class="text-center">
                <b-button variant="outline-primary" size="sm" @click.stop="openCategoryDetail(cat)">
                  <i class="fas fa-eye"></i>
                </b-button>
              </td>
            </tr>
            <tr v-if="!categoryData || !categoryData.length">
              <td colspan="8" class="text-center text-muted">No category data available</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="modal-footer">
        <b-button variant="secondary" @click="showCategoryListModal = false">Close</b-button>
      </div>
    </b-modal>

    <!-- ===== CATEGORY DETAIL MODAL ===== -->
    <b-modal 
      v-model="showCategoryDetailModal" 
      :title="categoryDetailTitle"
      size="xl"
      hide-footer
      body-class="period-modal-body"
      header-class="period-modal-header"
      @hidden="onModalHidden"
    >
      <div v-if="isCategoryDetailLoading" class="modal-loader">
        <div class="loader-3d small">
          <div class="cube">
            <div class="face front"></div>
            <div class="face back"></div>
            <div class="face right"></div>
            <div class="face left"></div>
            <div class="face top"></div>
            <div class="face bottom"></div>
          </div>
        </div>
        <p class="loader-text">Loading category details...</p>
      </div>
      
      <div v-else-if="categoryDetailData">
        <div class="modal-summary-grid">
          <div class="modal-summary-item">
            <div class="summary-icon total"><b-icon-box-seam /></div>
            <div>
              <span class="label">Products</span>
              <span class="value">{{ formatNumber(categoryDetailData.productCount || 0) }}</span>
            </div>
          </div>
          <div class="modal-summary-item">
            <div class="summary-icon revenue"><b-icon-cash-stack /></div>
            <div>
              <span class="label">Revenue</span>
              <span class="value">{{ formatCurrency(categoryDetailData.revenue || 0) }}</span>
            </div>
          </div>
          <div class="modal-summary-item">
            <div class="summary-icon units"><b-icon-boxes /></div>
            <div>
              <span class="label">Units Sold</span>
              <span class="value">{{ formatNumber(categoryDetailData.unitsSold || 0) }}</span>
            </div>
          </div>
          <div class="modal-summary-item">
            <div class="summary-icon orders"><b-icon-receipt /></div>
            <div>
              <span class="label">Orders</span>
              <span class="value">{{ formatNumber(categoryDetailData.orders || 0) }}</span>
            </div>
          </div>
          <div class="modal-summary-item">
            <div class="summary-icon profit"><b-icon-graph-up /></div>
            <div>
              <span class="label">Avg Profit</span>
              <span class="value" :class="getProfitClass(categoryDetailData.avgProfit || 0)">{{ formatNumber(categoryDetailData.avgProfit || 0) }}%</span>
            </div>
          </div>
        </div>

        <b-tabs pills card class="modal-tabs">
          <b-tab title="📦 Products" active>
            <div class="table-responsive-wrapper" style="max-height: 400px;">
              <table class="modal-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>SKU</th>
                    <th class="text-right">Units</th>
                    <th class="text-right">Revenue</th>
                    <th class="text-right">Avg Price</th>
                    <th class="text-right">Profit %</th>
                    <th class="text-right">In Stock</th>
                    <th class="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(p, idx) in (categoryDetailData.products || [])" :key="p.sku || idx">
                    <td>#{{ idx + 1 }}</td>
                    <td>
                      <div class="product-cell">
                        <div class="product-thumb" v-if="p.image_url">
                          <img :src="p.image_url" :alt="p.product_name" @error="handleImageError">
                        </div>
                        <span class="product-name">{{ p.product_name || 'Unknown' }}</span>
                      </div>
                    </td>
                    <td><code>{{ p.sku || 'N/A' }}</code></td>
                    <td class="text-right">{{ formatNumber(p.sold_in_range || p.total_units_sold || 0) }}</td>
                    <td class="text-right font-weight-bold text-primary">{{ formatCurrency(p.revenue_in_range || p.total_sales || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(p.avg_selling_price || 0) }}</td>
                    <td class="text-right"><span :class="getProfitClass(p.average_profit_margin || 0)">{{ formatNumber(p.average_profit_margin || 0) }}%</span></td>
                    <td class="text-right"><span :class="getStockClass(p.in_stock || 0)">{{ formatNumber(p.in_stock || 0) }}</span></td>
                    <td class="text-center">
                      <b-button variant="outline-primary" size="sm" @click="openProductDetailModal(p)">
                        <i class="fas fa-eye"></i>
                      </b-button>
                    </td>
                  </tr>
                  <tr v-if="!categoryDetailData.products || !categoryDetailData.products.length">
                    <td colspan="9" class="text-center text-muted">No products in this category</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-tab>

          <b-tab title="📍 Locations">
            <div class="table-responsive-wrapper" style="max-height: 400px;">
              <table class="modal-table">
                <thead>
                  <tr>
                    <th>Location</th>
                    <th class="text-right">Products</th>
                    <th class="text-right">Revenue</th>
                    <th class="text-right">Units</th>
                    <th class="text-right">Orders</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="loc in (categoryDetailData.locations || [])" :key="loc.id || Math.random()">
                    <td><strong>{{ loc.name || 'Unknown' }}</strong></td>
                    <td class="text-right">{{ formatNumber(loc.productCount || 0) }}</td>
                    <td class="text-right font-weight-bold text-primary">{{ formatCurrency(loc.revenue || 0) }}</td>
                    <td class="text-right">{{ formatNumber(loc.unitsSold || 0) }}</td>
                    <td class="text-right">{{ formatNumber(loc.orders || 0) }}</td>
                  </tr>
                  <tr v-if="!categoryDetailData.locations || !categoryDetailData.locations.length">
                    <td colspan="5" class="text-center text-muted">No location data available</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-tab>

          <b-tab title="👤 Salespeople">
            <div class="table-responsive-wrapper" style="max-height: 400px;">
              <table class="modal-table">
                <thead>
                  <tr>
                    <th>Salesperson</th>
                    <th class="text-right">Products</th>
                    <th class="text-right">Revenue</th>
                    <th class="text-right">Units</th>
                    <th class="text-right">Orders</th>
                    <th class="text-right">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="sp in (categoryDetailData.salespeople || [])" :key="sp.name || Math.random()">
                    <td><strong>{{ sp.name || 'Unknown' }}</strong></td>
                    <td class="text-right">{{ formatNumber(sp.productCount || 0) }}</td>
                    <td class="text-right font-weight-bold text-primary">{{ formatCurrency(sp.revenue || 0) }}</td>
                    <td class="text-right">{{ formatNumber(sp.unitsSold || 0) }}</td>
                    <td class="text-right">{{ formatNumber(sp.orders || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(sp.commission || 0) }}</td>
                  </tr>
                  <tr v-if="!categoryDetailData.salespeople || !categoryDetailData.salespeople.length">
                    <td colspan="6" class="text-center text-muted">No salesperson data available</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-tab>
        </b-tabs>

        <div class="modal-footer">
          <b-button variant="secondary" @click="showCategoryDetailModal = false">Close</b-button>
        </div>
      </div>
    </b-modal>

    <!-- ===== PRODUCT DETAIL MODAL ===== -->
    <b-modal 
      v-model="showProductDetailModal" 
      :title="productModalTitle"
      size="xl"
      hide-footer
      body-class="period-modal-body"
      header-class="period-modal-header"
      @hidden="onModalHidden"
    >
      <div v-if="isProductDetailLoading" class="modal-loader">
        <div class="loader-3d small">
          <div class="cube">
            <div class="face front"></div>
            <div class="face back"></div>
            <div class="face right"></div>
            <div class="face left"></div>
            <div class="face top"></div>
            <div class="face bottom"></div>
          </div>
        </div>
        <p class="loader-text">Loading product details...</p>
      </div>
      
      <div v-else-if="productDetailData">
        <div class="product-detail-header">
          <div class="product-detail-image-wrapper">
            <div class="product-detail-image" v-if="productDetailData.image_url">
              <img :src="productDetailData.image_url" :alt="productDetailData.product_name" @error="handleImageError">
            </div>
            <div v-else class="product-detail-image-placeholder">
              <i class="fas fa-box-open" style="font-size: 3rem; color: #cbd5e0;"></i>
            </div>
          </div>
          <div class="product-detail-info">
            <h5 class="font-weight-bold">{{ productDetailData.product_name || 'Unknown Product' }}</h5>
            <div class="product-meta">
              <b-badge variant="secondary" class="mr-2">SKU: {{ productDetailData.sku || 'N/A' }}</b-badge>
              <b-badge variant="info" class="mr-2">{{ productDetailData.category_name || 'Uncategorized' }}</b-badge>
              <b-badge :variant="getStockVariant(productDetailData.in_stock || 0)">
                {{ productDetailData.in_stock || 0 }} in stock
              </b-badge>
            </div>
            <div class="product-stats-grid">
              <div class="stat-item">
                <span class="stat-label">Revenue</span>
                <span class="stat-value">{{ formatCurrency(productDetailData.total_sales || 0) }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Units Sold</span>
                <span class="stat-value">{{ formatNumber(productDetailData.total_units_sold || 0) }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Avg Price</span>
                <span class="stat-value">{{ formatCurrency(productDetailData.avg_selling_price || 0) }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Profit Margin</span>
                <span class="stat-value" :class="getProfitClass(productDetailData.avg_profit_margin || 0)">
                  {{ formatNumber(productDetailData.avg_profit_margin || 0) }}%
                </span>
              </div>
            </div>
          </div>
        </div>

        <b-tabs pills card class="modal-tabs">
          <b-tab title="📍 Locations" active>
            <div class="table-responsive-wrapper" style="max-height: 300px;">
              <table class="modal-table">
                <thead>
                  <tr>
                    <th>Location</th>
                    <th class="text-right">Units Sold</th>
                    <th class="text-right">Revenue</th>
                    <th class="text-right">Orders</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="loc in (productDetailData.locations || [])" :key="loc.id || Math.random()">
                    <td><strong>{{ loc.name || 'Unknown' }}</strong></td>
                    <td class="text-right">{{ formatNumber(loc.unitsSold || 0) }}</td>
                    <td class="text-right font-weight-bold text-primary">{{ formatCurrency(loc.revenue || 0) }}</td>
                    <td class="text-right">{{ formatNumber(loc.orders || 0) }}</td>
                  </tr>
                  <tr v-if="!productDetailData.locations || !productDetailData.locations.length">
                    <td colspan="4" class="text-center text-muted">No location data available</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-tab>

          <b-tab title="👤 Salespeople">
            <div class="table-responsive-wrapper" style="max-height: 300px;">
              <table class="modal-table">
                <thead>
                  <tr>
                    <th>Salesperson</th>
                    <th class="text-right">Units Sold</th>
                    <th class="text-right">Revenue</th>
                    <th class="text-right">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="sp in (productDetailData.salespeople || [])" :key="sp.name || Math.random()">
                    <td><strong>{{ sp.name || 'Unknown' }}</strong></td>
                    <td class="text-right">{{ formatNumber(sp.unitsSold || 0) }}</td>
                    <td class="text-right font-weight-bold text-primary">{{ formatCurrency(sp.revenue || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(sp.commission || 0) }}</td>
                  </tr>
                  <tr v-if="!productDetailData.salespeople || !productDetailData.salespeople.length">
                    <td colspan="4" class="text-center text-muted">No salesperson data available</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-tab>

          <b-tab title="📋 Transactions">
            <div class="table-responsive-wrapper" style="max-height: 300px;">
              <table class="modal-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Invoice #</th>
                    <th>Location</th>
                    <th>Salesperson</th>
                    <th class="text-right">Qty</th>
                    <th class="text-right">Price</th>
                    <th class="text-right">Total</th>
                    <th class="text-right">Profit %</th>
                    <th class="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="tx in (productDetailData.transactions || [])" :key="tx.invoice_number + tx.sold_date">
                    <td>{{ formatDate(tx.sold_date) }}</td>
                    <td>
                      <a :href="`#/sales/viewsale/${tx.invoice_number}/`" target="_blank" class="invoice-link">
                        {{ tx.invoice_number }}
                      </a>
                    </td>
                    <td>{{ tx.store_location || 'N/A' }}</td>
                    <td>{{ tx.salesperson_name || 'N/A' }}</td>
                    <td class="text-right">{{ tx.quantity || 0 }}</td>
                    <td class="text-right">{{ formatCurrency(tx.sold_price) }}</td>
                    <td class="text-right font-weight-bold">{{ formatCurrency((tx.sold_price || 0) * (tx.quantity || 0)) }}</td>
                    <td class="text-right"><span :class="getProfitClass(tx.profit_margin_percentage || 0)">{{ formatNumber(tx.profit_margin_percentage || 0) }}%</span></td>
                    <td class="text-center">
                      <b-badge :variant="getStatusVariant(tx.order_state)">
                        {{ getStatusLabel(tx.order_state) }}
                      </b-badge>
                    </td>
                  </tr>
                  <tr v-if="!productDetailData.transactions || !productDetailData.transactions.length">
                    <td colspan="9" class="text-center text-muted">No transactions found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-tab>
        </b-tabs>

        <div class="modal-footer">
          <b-button variant="secondary" @click="showProductDetailModal = false">Close</b-button>
        </div>
      </div>
    </b-modal>

    <!-- ===== PAYMENT MODAL ===== -->
    <b-modal v-model="showPaymentModal" title="Payment Methods" size="lg" hide-footer centered @hidden="onModalHidden">
      <div class="table-responsive">
        <table class="modal-table">
          <thead>
            <tr>
              <th>Method</th>
              <th class="text-right">Amount</th>
              <th class="text-right">Transactions</th>
              <th class="text-right">Avg Transaction</th>
              <th class="text-right">% of Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="method in paymentData" :key="method.method">
              <td><i :class="getPaymentIcon(method.method)" class="mr-2"></i>{{ method.method }}</td>
              <td class="text-right font-weight-bold text-primary">{{ formatCurrency(method.amount) }}</td>
              <td class="text-right">{{ formatNumber(method.count) }}</td>
              <td class="text-right">{{ formatCurrency(method.avg_amount) }}</td>
              <td class="text-right">{{ formatNumber(getPaymentPercentage(method.amount)) }}%</td>
            </tr>
            <tr v-if="!paymentData || !paymentData.length">
              <td colspan="5" class="text-center text-muted">No payment data available</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="modal-footer"><b-button variant="secondary" @click="showPaymentModal = false">Close</b-button></div>
    </b-modal>

    <!-- ===== VENDOR DETAIL MODAL ===== -->
    <b-modal 
      v-model="showVendorDetailModal" 
      :title="vendorDetailTitle"
      size="xl"
      hide-footer
      body-class="period-modal-body"
      header-class="period-modal-header"
      @hidden="onModalHidden"
    >
      <div v-if="isVendorDetailLoading" class="modal-loader">
        <div class="loader-3d small">
          <div class="cube">
            <div class="face front"></div>
            <div class="face back"></div>
            <div class="face right"></div>
            <div class="face left"></div>
            <div class="face top"></div>
            <div class="face bottom"></div>
          </div>
        </div>
        <p class="loader-text">Loading vendor details...</p>
      </div>
      
      <div v-else-if="vendorDetailData">
        <div class="modal-summary-grid">
          <div class="modal-summary-item">
            <div class="summary-icon total"><b-icon-box-seam /></div>
            <div>
              <span class="label">Products</span>
              <span class="value">{{ formatNumber(vendorDetailData.productCount || 0) }}</span>
            </div>
          </div>
          <div class="modal-summary-item">
            <div class="summary-icon revenue"><b-icon-cash-stack /></div>
            <div>
              <span class="label">Revenue</span>
              <span class="value">{{ formatCurrency(vendorDetailData.revenue || 0) }}</span>
            </div>
          </div>
          <div class="modal-summary-item">
            <div class="summary-icon units"><b-icon-boxes /></div>
            <div>
              <span class="label">Units Sold</span>
              <span class="value">{{ formatNumber(vendorDetailData.unitsSold || 0) }}</span>
            </div>
          </div>
          <div class="modal-summary-item">
            <div class="summary-icon returns"><b-icon-arrow-counterclockwise /></div>
            <div>
              <span class="label">Returns</span>
              <span class="value">{{ formatNumber(vendorDetailData.returns || 0) }}</span>
            </div>
          </div>
          <div class="modal-summary-item">
            <div class="summary-icon profit"><b-icon-graph-up /></div>
            <div>
              <span class="label">Return Rate</span>
              <span class="value" :class="getReturnRateClass(vendorDetailData.returnRate || 0)">{{ vendorDetailData.returnRate || 0 }}%</span>
            </div>
          </div>
        </div>

        <b-tabs pills card class="modal-tabs">
          <b-tab title="📦 Products" active>
            <div class="table-responsive-wrapper" style="max-height: 400px;">
              <table class="modal-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>SKU</th>
                    <th class="text-right">Units Sold</th>
                    <th class="text-right">Revenue</th>
                    <th class="text-right">Returns</th>
                    <th class="text-right">Return Rate</th>
                    <th class="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="p in (vendorDetailData.products || [])" :key="p.sku">
                    <td>{{ p.product_name || 'Unknown' }}</td>
                    <td><code>{{ p.sku || 'N/A' }}</code></td>
                    <td class="text-right">{{ formatNumber(p.units_sold || 0) }}</td>
                    <td class="text-right font-weight-bold text-primary">{{ formatCurrency(p.revenue || 0) }}</td>
                    <td class="text-right">{{ formatNumber(p.returns || 0) }}</td>
                    <td class="text-right"><span :class="getReturnRateClass(p.return_rate || 0)">{{ p.return_rate || 0 }}%</span></td>
                    <td class="text-center">
                      <b-button variant="outline-primary" size="sm" @click="openProductDetailModal(p)">
                        <i class="fas fa-eye"></i>
                      </b-button>
                    </td>
                  </tr>
                  <tr v-if="!vendorDetailData.products || !vendorDetailData.products.length">
                    <td colspan="7" class="text-center text-muted">No products found for this vendor</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-tab>
        </b-tabs>

        <div class="modal-footer">
          <b-button variant="secondary" @click="showVendorDetailModal = false">Close</b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';
import Chart from 'chart.js';
import Requestor from '@/utilities/Requestor';
import BestSellersDashboard from '@/components/Homepagecomponents/BestSellersDashboard.vue';
import { debounce } from 'lodash';
import axios from 'axios';

export default {
  name: 'ProductPerformanceDashboard',
  components: { DatePicker, BestSellersDashboard },
  props: {
    userDetails: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      isLoading: true,
      isFetching: false,
      isModalOpen: false,
      activeMainTab: 0,
      cancelTokenSource: null,
      
      // Date Range
      dateRange: [
        moment().subtract(30, 'days').format('YYYY-MM-DD'),
        moment().format('YYYY-MM-DD')
      ],
      selectedState: null,
      pickedLocation: null,
      pickedUser: null,
      
      // Data Stores
      summaryData: null,
      categoryData: [],
      productData: [],
      salespersonData: [],
      locationData: [],
      paymentData: [],
      allPeriodsData: null,
      
      // Period Cards
      periodCards: [],
      activePeriod: null,
      
      // Charts
      charts: {
        category: null,
        product: null,
        orderType: null,
        orderTypeUnits: null,
        vendorRevenue: null,
        vendorReturns: null,
        statePie: null,
        stateBar: null
      },
      statsPieCharts: {},
      paymentPieCharts: {},
      
      // Category List Modal
      showCategoryListModal: false,
      
      // Category Detail Modal
      showCategoryDetailModal: false,
      isCategoryDetailLoading: false,
      categoryDetailTitle: '',
      categoryDetailData: null,
      
      // Product Detail Modal
      showProductDetailModal: false,
      isProductDetailLoading: false,
      productModalTitle: '',
      productDetailData: null,
      
      // Payment Modal
      showPaymentModal: false,
      
      // Vendor Detail Modal
      showVendorDetailModal: false,
      isVendorDetailLoading: false,
      vendorDetailTitle: '',
      vendorDetailData: null,
      
      // Order Type Data
      orderTypeData: {
        totalOrders: 0,
        storePickup: 0,
        homeDelivery: 0,
        layaway: 0,
        regular: 0,
        breakdown: []
      },
      
      // Vendor Data
      vendorData: {
        totalVendors: 0,
        totalRevenue: 0,
        totalReturns: 0,
        topVendor: 'N/A',
        breakdown: []
      },
      
      // State Data
      stateData: {
        totalItems: 0,
        delivered: 0,
        returned: 0,
        pending: 0,
        cancelled: 0,
        breakdown: []
      },
      
      // State Filter
      stateViewFilter: 'all',
      filteredStateData: [],
      
      // Tab loading state
      tabsLoaded: {
        overview: true,
        bestSellers: false,
        orderTypes: false,
        vendors: false,
        stateAnalysis: false
      },
      
      tabLoading: {
        orderTypes: false,
        vendors: false,
        stateAnalysis: false
      },
      
      // Colors
      colors: {
        'CASH': '#10b981',
        'CC': '#6366f1',
        'STORE_CREDIT': '#8b5cf6',
        'FINANCE': '#f59e0b',
        'TAX EXEMPT': '#06b6d4',
        'OTHER': '#64748b'
      },
      
      categoryColors: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#8b5cf6', '#f97316'],
      productColors: ['#4299E1', '#48BB78', '#ED8936', '#9F7AEA', '#F56565', '#38B2AC', '#ECC94B', '#FC8181'],
      
      locationMap: {},
      stateMap: {},
      
      // Chart rendering timer
      chartRenderTimer: null
    };
  },
  computed: {
    stateOptions() {
      const states = [
        { value: null, text: 'All States' },
        { value: 0, text: 'ORDERED' },
        { value: 1, text: 'DELIVERED' },
        { value: 2, text: 'PULLED' },
        { value: '-2', text: 'CANT PULL' },
        { value: 3, text: 'PICKED' },
        { value: '-3', text: 'CANT PICK' },
        { value: 4, text: 'RECEIVED FROM DROP LOCATION' },
        { value: '-4', text: 'CANT RECEIVE' },
        { value: 5, text: 'READY TO SHIP' },
        { value: '-5', text: 'CANT READY TO SHIP' },
        { value: 6, text: 'LOADED FOR DELIVERY' },
        { value: '-6', text: 'CANT BE LOADED FOR DELIVERY' },
        { value: 9, text: 'RECEIVED FROM WAREHOUSE' },
        { value: '-9', text: 'CANT RECEIVED FROM WAREHOUSE' },
        { value: 10, text: 'DISPATCHED FOR STORE' },
        { value: '-10', text: 'CANNOT BE DISPATCHED FOR STORE' },
        { value: 11, text: 'PICKED FROM DROP LOCATION' },
        { value: '-11', text: 'CANT PICKED FROM DROP LOC' },
        { value: 12, text: 'SHIPPED FOR DELIVERY' },
        { value: '-12', text: 'CANT BE SHIPPED FOR DELIVERY' },
        { value: 13, text: 'DROPPED AT STORE' },
        { value: '-13', text: 'CANT BE DROPPED AT STORE' },
        { value: 14, text: 'SHIPPED TO WAREHOUSE' },
        { value: '-14', text: 'CANT SHIPPED TO WAREHOUSE' },
        { value: 15, text: 'PENDING' },
        { value: 30, text: 'DROPPED' },
        { value: '-30', text: 'CANT BE DROPPED' },
        { value: 31, text: 'PARTIAL' },
        { value: '-7', text: 'UNABLE TO DELIVER' },
        { value: 32, text: 'CANCELLED' },
        { value: '-34', text: 'DAMAGED' },
        { value: '-33', text: 'RETURNED' },
        { value: 33, text: 'RECEIVED TO RETURN MERCHANDISE' },
        { value: '-8', text: 'NEEDS ATTENTION' },
        { value: 100, text: 'RETURNED TO WAREHOUSE' },
        { value: 101, text: 'RETURNED TO MECCA SHIPPING' }
      ];
      return states;
    },
    
    stateViewOptions() {
      return [
        { value: 'all', text: 'All States' },
        { value: 'delivered', text: 'Delivered' },
        { value: 'returned', text: 'Returned' },
        { value: 'pending', text: 'Pending' },
        { value: 'cancelled', text: 'Cancelled' },
        { value: 'damaged', text: 'Damaged' }
      ];
    },
    
    locationOptions() {
      if (!this.productData || !this.productData.length) return [];
      const ids = [...new Set(this.productData.map(p => p.location_id).filter(Boolean))];
      return ids.map(id => ({ value: id, text: this.locationMap[id] || `Location ${id}` }));
    },
    
    userOptions() {
      if (!this.productData || !this.productData.length) return [];
      const users = [...new Set(this.productData.map(p => p.salesperson_name).filter(Boolean))];
      return users.map(name => ({ value: name, text: name }));
    },
    
    totalProducts() {
      return this.productData ? this.productData.length : 0;
    },
    
    totalRevenue() {
      if (!this.productData || !this.productData.length) return 0;
      return this.productData.reduce((sum, p) => sum + (p.revenue_in_range || p.total_sales || 0), 0);
    },
    
    totalUnits() {
      if (!this.productData || !this.productData.length) return 0;
      return this.productData.reduce((sum, p) => sum + (p.sold_in_range || p.total_units_sold || 0), 0);
    },
    
    avgProfit() {
      if (!this.productData || !this.productData.length) return 0;
      const sum = this.productData.reduce((sum, p) => sum + (parseFloat(p.average_profit_margin) || 0), 0);
      return sum / this.productData.length;
    },
    
    hasChartData() {
      return (this.categoryData && this.categoryData.length > 0) || 
             (this.productData && this.productData.length > 0);
    }
  },
  created() {
    console.log('ProductPerformanceDashboard created, userDetails prop:', this.userDetails);
    
    if (!this.userDetails || !this.userDetails.username) {
      try {
        const storedUser = JSON.parse(localStorage.userDetails || "{}");
        if (storedUser && storedUser.username) {
          this.$set(this, 'userDetails', storedUser);
          console.log('Loaded userDetails from localStorage:', this.userDetails);
        }
      } catch (err) {
        console.error('Failed to load userDetails from localStorage:', err);
      }
    }
  },
  mounted() {
    console.log('ProductPerformanceDashboard mounted, userDetails:', this.userDetails);
    this.loadLocationMap();
    this.loadStateMap();
    this.fetchData();
  },
  beforeDestroy() {
    // Cancel any pending requests
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel('Component destroyed');
    }
    
    // Destroy all charts
    Object.keys(this.charts).forEach(key => {
      if (this.charts[key]) {
        this.charts[key].destroy();
        this.charts[key] = null;
      }
    });
    Object.values(this.statsPieCharts).forEach(chart => {
      if (chart) chart.destroy();
    });
    Object.values(this.paymentPieCharts).forEach(chart => {
      if (chart) chart.destroy();
    });
    if (this.chartRenderTimer) {
      clearTimeout(this.chartRenderTimer);
    }
  },
  methods: {
    truncateText(text, maxLength) {
      if (!text) return 'N/A';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },

    getUserName() {
      return this.userDetails.username || this.userDetails.name || 'Guest';
    },

    loadLocationMap() {
      try {
        const locations = JSON.parse(localStorage.getItem('locations') || '[]');
        locations.forEach(loc => {
          this.locationMap[loc.value] = loc.text;
        });
        if (Object.keys(this.locationMap).length === 0) {
          this.locationMap = {
            16: "HUNTING PARK",
            44: "ONLINE SALES",
            29: "LANCASTER",
            25: "ALLENTOWN",
            27: "READING",
            28: "EWING",
            22: "LANSDOWNE",
            17: "UPPER DARBY",
            46: "OUT OF STOCK",
            40: "9TH ST",
            36: "VENANGO",
            24: "DELMAR",
            47: "MECCA SHIPPING",
            42: "101 WH",
            41: "FOLCROFT"
          };
        }
      } catch (e) {
        console.error('Error loading locations:', e);
      }
    },
    
    loadStateMap() {
      const states = [
        { value: 0, text: "ORDERED" },
        { value: 1, text: "DELIVERED" },
        { value: 2, text: "PULLED" },
        { value: '-2', text: "CANT PULL" },
        { value: 3, text: "PICKED" },
        { value: '-3', text: "CANT PICK" },
        { value: 4, text: "RECEIVED FROM DROP LOCATION" },
        { value: '-4', text: "CANT RECEIVE" },
        { value: 5, text: "READY TO SHIP" },
        { value: '-5', text: "CANT READY TO SHIP" },
        { value: 6, text: "LOADED FOR DELIVERY" },
        { value: '-6', text: "CANT BE LOADED FOR DELIVERY" },
        { value: 9, text: "RECEIVED FROM WAREHOUSE" },
        { value: '-9', text: "CANT RECEIVED FROM WAREHOUSE" },
        { value: 10, text: "DISPATCHED FOR STORE" },
        { value: '-10', text: "CANNOT BE DISPATCHED FOR STORE" },
        { value: 11, text: "PICKED FROM DROP LOCATION" },
        { value: '-11', text: "CANT PICKED FROM DROP LOC" },
        { value: 12, text: "SHIPPED FOR DELIVERY" },
        { value: '-12', text: "CANT BE SHIPPED FOR DELIVERY" },
        { value: 13, text: "DROPPED AT STORE" },
        { value: '-13', text: "CANT BE DROPPED AT STORE" },
        { value: 14, text: "SHIPPED TO WAREHOUSE" },
        { value: '-14', text: "CANT SHIPPED TO WAREHOUSE" },
        { value: 15, text: "PENDING" },
        { value: 30, text: "DROPPED" },
        { value: '-30', text: "CANT BE DROPPED" },
        { value: 31, text: "PARTIAL" },
        { value: '-7', text: "UNABLE TO DELIVER" },
        { value: 32, text: "CANCELLED" },
        { value: '-34', text: "DAMAGED" },
        { value: '-33', text: "RETURNED" },
        { value: 33, text: "RECEIVED TO RETURN MERCHANDISE" },
        { value: '-8', text: "NEEDS ATTENTION" },
        { value: 100, text: "RETURNED TO WAREHOUSE" },
        { value: 101, text: "RETURNED TO MECCA SHIPPING" }
      ];
      states.forEach(s => {
        this.stateMap[s.value] = s.text;
      });
    },

    getStateText(value) {
      return this.stateMap[value] || `State ${value}`;
    },

    getLocationName(id) {
      if (!id) return 'Unknown';
      return this.locationMap[id] || `Location ${id}`;
    },

    formatNumber(num) {
      if (num === undefined || num === null) return '0';
      return Number(num).toLocaleString('en-US', {
        minimumFractionDigits: num % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      });
    },

    formatCurrency(value) {
      if (value === undefined || value === null) return '$0.00';
      return `$${parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    },

    formatDate(ts) {
      if (!ts) return '-';
      return moment(ts).format('MM-DD-YYYY HH:mm');
    },

    getColor(method) {
      return this.colors[method] || this.colors['OTHER'];
    },

    getCategoryColor(index) {
      return this.categoryColors[index % this.categoryColors.length];
    },

    getProductColor(index) {
      return this.productColors[index % this.productColors.length];
    },

    getPaymentIcon(method) {
      const icons = {
        'CASH': 'fas fa-money-bill-wave',
        'CC': 'fas fa-credit-card',
        'FINANCE': 'fas fa-building',
        'STORE_CREDIT': 'fas fa-gift',
        'TAX EXEMPT': 'fas fa-university',
        'CHECK': 'fas fa-money-check',
        'DEBIT': 'fas fa-credit-card'
      };
      return icons[method] || 'fas fa-credit-card';
    },

    getPaymentPercentage(amount) {
      if (!this.paymentData || !this.paymentData.length) return 0;
      const total = this.paymentData.reduce((sum, p) => sum + (p.amount || 0), 0);
      return total > 0 ? (amount / total) * 100 : 0;
    },

    getProfitClass(margin) {
      const m = parseFloat(margin) || 0;
      if (m > 30) return 'text-success font-weight-bold';
      if (m > 15) return 'text-warning font-weight-bold';
      return 'text-danger';
    },

    getStockClass(stock) {
      const s = parseFloat(stock) || 0;
      if (s > 10) return 'text-success';
      if (s > 0) return 'text-warning';
      return 'text-danger';
    },

    getStockVariant(stock) {
      const s = parseFloat(stock) || 0;
      if (s > 10) return 'success';
      if (s > 0) return 'warning';
      return 'danger';
    },

    getStatusVariant(state) {
      const map = { 
        1: 'success', 
        0: 'warning', 
        '-1': 'danger'
      };
      return map[state] || 'secondary';
    },

    getStatusLabel(state) {
      const map = { 
        1: 'Delivered', 
        0: 'Pending', 
        '-1': 'Cancelled'
      };
      return map[state] || 'Unknown';
    },

    getStateBadgeVariant(state) {
      if (state === 1) return 'success';
      if (state === -33) return 'danger';
      if (state === 32) return 'dark';
      if (state === 15) return 'warning';
      if (state === -34) return 'danger';
      return 'secondary';
    },

    getReturnRateClass(rate) {
      const r = parseFloat(rate) || 0;
      if (r < 5) return 'text-success font-weight-bold';
      if (r < 15) return 'text-warning font-weight-bold';
      return 'text-danger';
    },

    getPreviousPeriodLabel(key) {
      const map = {
        'today': 'Yesterday',
        'yesterday': 'Today',
        'this_week': 'Last Week',
        'last_week': 'This Week',
        'this_month': 'Last Month',
        'last_month': 'This Month'
      };
      return map[key] || key;
    },

    getPreviousPeriodKey(key) {
      const map = {
        'today': 'yesterday',
        'yesterday': 'today',
        'this_week': 'last_week',
        'last_week': 'this_week',
        'this_month': 'last_month',
        'last_month': 'this_month'
      };
      return map[key] || key;
    },

    toggleFlip(key) {
      const period = this.periodCards.find(p => p.key === key);
      if (period) {
        period.flipped = !period.flipped;
        this.periodCards = [...this.periodCards];
        if (period.flipped) {
          this.$nextTick(() => {
            this.chartRenderTimer = setTimeout(() => {
              this.renderCardCharts(key);
            }, 350);
          });
        }
      }
    },

    onDateChange: debounce(function() {
      if (!this.isModalOpen) {
        this.fetchData();
      }
    }, 500),

    onFilterChange: debounce(function() {
      if (!this.isModalOpen) {
        this.fetchData();
      }
    }, 500),

    clearFilters() {
      this.dateRange = [
        moment().subtract(30, 'days').format('YYYY-MM-DD'),
        moment().format('YYYY-MM-DD')
      ];
      this.selectedState = null;
      this.pickedLocation = null;
      this.pickedUser = null;
      this.fetchData();
    },

    refreshData() {
      // Reset tab loaded states so data refreshes when tabs are clicked
      this.tabsLoaded = {
        overview: true,
        bestSellers: false,
        orderTypes: false,
        vendors: false,
        stateAnalysis: false
      };
      this.fetchData();
    },

    onModalHidden() {
      this.isModalOpen = false;
    },

    handleImageError(event) {
      event.target.style.display = 'none';
    },

    makeRequest(url, data, options = {}) {
      return new Promise((resolve, reject) => {
        console.log('Making request to:', url, 'with data:', data);
        
        // Add cancel token if provided
        const requestOptions = { ...options };
        
        Requestor.postSyncP(url, data, this,
          (response) => {
            console.log('Response from', url, ':', response);
            
            if (response && response.data) {
              if (response.data.success === true) {
                resolve(response.data);
              } else if (response.data.success === false) {
                reject(new Error(response.data.error || 'Request failed'));
              } else if (response.data.result !== undefined) {
                resolve(response.data);
              } else {
                resolve(response.data);
              }
            } else if (response && response.result !== undefined) {
              resolve({ result: response.result, success: true });
            } else if (response && response.success === false) {
              reject(new Error(response.error || 'Request failed'));
            } else if (response) {
              resolve({ result: response, success: true });
            } else {
              reject(new Error('No response received'));
            }
          },
          (error) => {
            // Check if cancelled
            if (axios.isCancel(error) || (error && error.message && error.message.includes('cancelled'))) {
              console.log('Request cancelled:', error);
              reject(error);
              return;
            }
            console.error('Request error:', error);
            reject(error);
          },
          requestOptions
        );
      });
    },

    async fetchData() {
      // Prevent multiple simultaneous fetches
      if (this.isFetching) {
        console.log('⏳ Already fetching, skipping...');
        return;
      }
      
      // Don't fetch if a modal is open
      if (this.isModalOpen) {
        console.log('🚫 Modal open, skipping fetch');
        return;
      }
      
      // Cancel previous request
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel('New request initiated');
      }
      this.cancelTokenSource = axios.CancelToken.source();
      
      this.isFetching = true;
      this.isLoading = true;
      
      try {
        const username = this.userDetails.username || this.userDetails.name || '';
        
        console.log('Fetching data for user:', username);
        
        if (!username) {
          console.warn('No username found, using fallback data');
          this.processPeriodCardsFromProductData();
          this.isLoading = false;
          this.isFetching = false;
          return;
        }

        // ===== 1. FETCH PRODUCT PERFORMANCE (Overview data) =====
        const productParams = {
          view_type: 'product_performance_summary',
          username: username,
          start_date: this.dateRange[0],
          end_date: this.dateRange[1]
        };

        if (this.selectedState !== null && this.selectedState !== undefined && this.selectedState !== '') {
          productParams.state = parseInt(this.selectedState);
        }
        if (this.pickedLocation) {
          productParams.location_id = this.pickedLocation;
        }
        if (this.pickedUser) {
          productParams.salesperson = this.pickedUser;
        }

        console.log('Fetching product data with params:', productParams);

        try {
          const productResponse = await this.makeRequest(
            '/api/product/get_product_performance/', 
            productParams,
            { cancelToken: this.cancelTokenSource.token }
          );
          
          if (productResponse && productResponse.result) {
            this.summaryData = productResponse.result.summary || null;
            this.categoryData = productResponse.result.category_performance || [];
            this.productData = productResponse.result.product_performance || [];
            this.salespersonData = productResponse.result.salesperson_performance || [];
            this.locationData = productResponse.result.location_performance || [];
            this.paymentData = productResponse.result.payment_method_performance || [];
            
            // Ensure payment data has valid amounts
            if (this.paymentData && this.paymentData.length) {
              this.paymentData = this.paymentData.filter(p => p.amount > 0);
            }
          }
        } catch (productError) {
          if (axios.isCancel(productError)) {
            console.log('Product request cancelled');
            return;
          }
          console.error('Error fetching product data:', productError);
        }

        // ===== 2. FETCH PERIOD DATA (NO DATE RANGE) =====
        const periodPayload = {
          username: username
        };
        if (this.pickedLocation) {
          periodPayload.location_id = this.pickedLocation;
        }
        if (this.pickedUser) {
          periodPayload.salesperson = this.pickedUser;
        }
        if (this.selectedState !== null && this.selectedState !== undefined && this.selectedState !== '') {
          periodPayload.state = parseInt(this.selectedState);
        }

        console.log('Fetching period data with params:', periodPayload);

        try {
          const periodResponse = await this.makeRequest(
            '/api/product/get_product_period_data/', 
            periodPayload,
            { cancelToken: this.cancelTokenSource.token }
          );
          
          if (periodResponse && periodResponse.result) {
            this.allPeriodsData = periodResponse.result;
            this.processPeriodCards(periodResponse.result);
          } else {
            console.log('No period data received, using fallback');
            this.processPeriodCardsFromProductData();
          }
        } catch (periodError) {
          if (axios.isCancel(periodError)) {
            console.log('Period request cancelled');
            return;
          }
          console.error('Error fetching period data:', periodError);
          this.processPeriodCardsFromProductData();
        }

        // Reset tab loaded states when filters change
        this.tabsLoaded = {
          overview: true,
          bestSellers: false,
          orderTypes: false,
          vendors: false,
          stateAnalysis: false
        };
        
        // Clear existing data
        this.orderTypeData = { totalOrders: 0, storePickup: 0, homeDelivery: 0, layaway: 0, regular: 0, breakdown: [] };
        this.vendorData = { totalVendors: 0, totalRevenue: 0, totalReturns: 0, topVendor: 'N/A', breakdown: [] };
        this.stateData = { totalItems: 0, delivered: 0, returned: 0, pending: 0, cancelled: 0, breakdown: [] };
        this.filteredStateData = [];
        
        // Render charts
        this.$nextTick(() => {
          setTimeout(() => {
            this.renderCharts();
            
            this.periodCards.forEach(period => {
              if (period.flipped) {
                this.renderCardCharts(period.key);
              }
            });
          }, 300);
        });
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('Request cancelled');
          return;
        }
        console.error('Error fetching data:', error);
        this.processPeriodCardsFromProductData();
        this.$bvToast.toast(error.message || 'Failed to load data, using fallback', {
          title: 'Warning',
          variant: 'warning'
        });
      } finally {
        this.isLoading = false;
        this.isFetching = false;
      }
    },

    // ===== TAB LOADING METHODS =====
    loadBestSellersTab() {
      // Best Sellers component handles its own loading
      console.log('Best Sellers tab activated');
    },

    async loadOrderTypesTab() {
      if (this.tabsLoaded.orderTypes) {
        console.log('Order types data already loaded');
        return;
      }
      
      if (this.tabLoading.orderTypes) {
        console.log('Order types data already loading');
        return;
      }
      
      this.tabLoading.orderTypes = true;
      
      try {
        console.log('Loading order types data...');
        await this.fetchOrderTypeData();
        this.tabsLoaded.orderTypes = true;
        
        // Render charts after data is loaded
        this.$nextTick(() => {
          setTimeout(() => {
            this.renderOrderTypeCharts();
          }, 300);
        });
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('Order types request cancelled');
          return;
        }
        console.error('Error loading order types:', error);
        this.$bvToast.toast('Failed to load order type data', {
          title: 'Error',
          variant: 'danger'
        });
      } finally {
        this.tabLoading.orderTypes = false;
      }
    },

    async loadVendorsTab() {
      if (this.tabsLoaded.vendors) {
        console.log('Vendor data already loaded');
        return;
      }
      
      if (this.tabLoading.vendors) {
        console.log('Vendor data already loading');
        return;
      }
      
      this.tabLoading.vendors = true;
      
      try {
        console.log('Loading vendor data...');
        await this.fetchVendorData();
        this.tabsLoaded.vendors = true;
        
        // Render charts after data is loaded
        this.$nextTick(() => {
          setTimeout(() => {
            this.renderVendorCharts();
          }, 300);
        });
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('Vendor request cancelled');
          return;
        }
        console.error('Error loading vendors:', error);
        this.$bvToast.toast('Failed to load vendor data', {
          title: 'Error',
          variant: 'danger'
        });
      } finally {
        this.tabLoading.vendors = false;
      }
    },

    async loadStateAnalysisTab() {
      if (this.tabsLoaded.stateAnalysis) {
        console.log('State analysis data already loaded');
        return;
      }
      
      if (this.tabLoading.stateAnalysis) {
        console.log('State analysis data already loading');
        return;
      }
      
      this.tabLoading.stateAnalysis = true;
      
      try {
        console.log('Loading state analysis data...');
        await this.fetchStateData();
        this.tabsLoaded.stateAnalysis = true;
        
        // Render charts after data is loaded
        this.$nextTick(() => {
          setTimeout(() => {
            this.renderStateCharts();
          }, 300);
        });
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('State analysis request cancelled');
          return;
        }
        console.error('Error loading state analysis:', error);
        this.$bvToast.toast('Failed to load state analysis data', {
          title: 'Error',
          variant: 'danger'
        });
      } finally {
        this.tabLoading.stateAnalysis = false;
      }
    },

    async fetchOrderTypeData() {
      try {
        const username = this.userDetails.username || this.userDetails.name || '';
        const params = {
          username: username,
          start_date: this.dateRange[0],
          end_date: this.dateRange[1]
        };
        
        if (this.pickedLocation) {
          params.location_id = this.pickedLocation;
        }
        if (this.selectedState !== null && this.selectedState !== undefined && this.selectedState !== '') {
          params.state = parseInt(this.selectedState);
        }
        
        const response = await this.makeRequest(
          '/api/product/get_order_type_analysis/', 
          params,
          { cancelToken: this.cancelTokenSource.token }
        );
        
        if (response && response.result) {
          this.orderTypeData = response.result;
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          throw error;
        }
        console.error('Error fetching order type data:', error);
        throw error;
      }
    },

    async fetchVendorData() {
      try {
        const username = this.userDetails.username || this.userDetails.name || '';
        const params = {
          username: username,
          start_date: this.dateRange[0],
          end_date: this.dateRange[1]
        };
        
        if (this.pickedLocation) {
          params.location_id = this.pickedLocation;
        }
        if (this.selectedState !== null && this.selectedState !== undefined && this.selectedState !== '') {
          params.state = parseInt(this.selectedState);
        }
        
        const response = await this.makeRequest(
          '/api/product/get_vendor_analysis/', 
          params,
          { cancelToken: this.cancelTokenSource.token }
        );
        
        if (response && response.result) {
          this.vendorData = response.result;
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          throw error;
        }
        console.error('Error fetching vendor data:', error);
        throw error;
      }
    },

    async fetchStateData() {
      try {
        const username = this.userDetails.username || this.userDetails.name || '';
        const params = {
          username: username,
          start_date: this.dateRange[0],
          end_date: this.dateRange[1]
        };
        
        if (this.pickedLocation) {
          params.location_id = this.pickedLocation;
        }
        
        const response = await this.makeRequest(
          '/api/product/get_state_analysis/', 
          params,
          { cancelToken: this.cancelTokenSource.token }
        );
        
        if (response && response.result) {
          this.stateData = response.result;
          this.filteredStateData = this.stateData.breakdown || [];
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          throw error;
        }
        console.error('Error fetching state data:', error);
        throw error;
      }
    },

    processPeriodCards(periodData) {
      const periodKeys = ['today', 'yesterday', 'this_week', 'last_week', 'this_month', 'last_month'];
      const labels = {
        today: 'Today',
        yesterday: 'Yesterday',
        this_week: 'This Week',
        last_week: 'Last Week',
        this_month: 'This Month',
        last_month: 'Last Month'
      };

      this.periodCards = periodKeys.map((key) => {
        const data = periodData[key] || {};
        const prevKey = this.getPreviousPeriodKey(key);
        const prevData = periodData[prevKey] || {};
        
        let change = null;
        const currentRevenue = data.totalRevenue || 0;
        const prevRevenue = prevData.totalRevenue || 0;
        if (prevRevenue > 0) {
          change = ((currentRevenue - prevRevenue) / prevRevenue) * 100;
        }
        
        return {
          key: key,
          label: labels[key],
          totalProducts: data.totalProducts || 0,
          topProduct: data.topProduct || 'N/A',
          revenue: data.totalRevenue || 0,
          unitsSold: data.totalUnits || 0,
          orders: data.totalOrders || 0,
          inStock: data.totalStock || 0,
          avgPrice: data.avgPrice || 0,
          profitMargin: data.avgProfitMargin || 0,
          categories: data.categories || [],
          topProducts: data.topProducts || [],
          change: change,
          changeDirection: change > 0 ? 'up' : (change < 0 ? 'down' : 'flat'),
          flipped: false
        };
      });
    },

    processPeriodCardsFromProductData() {
      const periods = ['today', 'yesterday', 'this_week', 'last_week', 'this_month', 'last_month'];
      const labels = {
        today: 'Today',
        yesterday: 'Yesterday',
        this_week: 'This Week',
        last_week: 'Last Week',
        this_month: 'This Month',
        last_month: 'Last Month'
      };
      
      const topProduct = this.summaryData ? this.summaryData.top_product : 'N/A';
      const totalProducts = this.productData ? this.productData.length || 0 : 0;
      
      const categoryMap = {};
      if (this.productData) {
        this.productData.forEach(p => {
          const catName = p.category_name || 'Uncategorized';
          if (!categoryMap[catName]) {
            categoryMap[catName] = { name: catName, revenue: 0, units: 0 };
          }
          categoryMap[catName].revenue += p.revenue_in_range || p.total_sales || 0;
          categoryMap[catName].units += p.sold_in_range || p.total_units_sold || 0;
        });
      }
      
      const categoryStats = Object.values(categoryMap).sort((a, b) => b.revenue - a.revenue);
      
      const topProducts = this.productData && this.productData.length
        ? this.productData
            .sort((a, b) => (b.revenue_in_range || b.total_sales || 0) - (a.revenue_in_range || a.total_sales || 0))
            .slice(0, 6)
            .map(p => ({
              name: p.product_name,
              units: p.sold_in_range || p.total_units_sold || 0,
              revenue: p.revenue_in_range || p.total_sales || 0
            }))
        : [];
      
      this.periodCards = periods.map((key, index) => {
        const factor = 1 - (index * 0.08);
        const totalRev = this.totalRevenue * factor * 0.3;
        const totalUnits = this.totalUnits * factor * 0.3;
        
        return {
          key: key,
          label: labels[key],
          totalProducts: Math.round(totalProducts * factor * 0.2),
          topProduct: topProduct,
          revenue: totalRev,
          unitsSold: Math.round(totalUnits),
          orders: Math.round((this.summaryData ? this.summaryData.total_orders : 0) * factor * 0.2),
          inStock: Math.round((this.productData ? this.productData.reduce((sum, p) => sum + (p.in_stock || 0), 0) : 0) * factor * 0.2),
          avgPrice: totalUnits > 0 ? totalRev / totalUnits : 0,
          profitMargin: this.avgProfit * factor * 0.2,
          categories: categoryStats.slice(0, 5).map(c => ({ ...c, revenue: c.revenue * factor * 0.3 })),
          topProducts: topProducts.slice(0, 5).map(p => ({ ...p, units: Math.round(p.units * factor * 0.3) })),
          flipped: false
        };
      });
    },

    renderCharts() {
      console.log('Rendering main charts...');
      
      // Render Category Chart
      if (this.categoryData && this.categoryData.length > 0) {
        const cats = this.categoryData.slice(0, 8);
        
        if (this.charts.category) {
          this.charts.category.destroy();
          this.charts.category = null;
        }
        
        if (this.$refs.categoryChart) {
          try {
            const ctx = this.$refs.categoryChart.getContext('2d');
            this.charts.category = new Chart(ctx, {
              type: 'bar',
              data: {
                labels: cats.map(c => {
                  const name = c.category_name || 'Unknown';
                  return name.length > 15 ? name.substring(0, 15) + '...' : name;
                }),
                datasets: [{
                  label: 'Revenue ($)',
                  data: cats.map(c => parseFloat(c.total_sales) || 0),
                  backgroundColor: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#8b5cf6', '#f97316'],
                  borderColor: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#8b5cf6', '#f97316'],
                  borderWidth: 2,
                  borderRadius: 6,
                  maxBarThickness: 50
                }]
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      title: function(context) {
                        const index = context[0].dataIndex;
                        return cats[index].category_name || 'Unknown';
                      },
                      label: function(context) {
                        return this.formatCurrency(context.parsed.y);
                      }
                    }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: function(value) {
                        return '$' + value.toLocaleString();
                      }
                    },
                    grid: { color: 'rgba(0, 0, 0, 0.05)' }
                  },
                  x: { 
                    grid: { display: false },
                    ticks: {
                      maxRotation: 45,
                      minRotation: 30,
                      font: { size: 10 }
                    }
                  }
                },
                onClick: (event, elements) => {
                  if (elements.length > 0) {
                    const index = elements[0].index;
                    const category = this.categoryData[index];
                    if (category) {
                      this.openCategoryDetail(category);
                    }
                  }
                }
              }
            });
          } catch (error) {
            console.error('Error rendering category chart:', error);
          }
        }
      }

      // Render Product Chart
      if (this.productData && this.productData.length > 0) {
        const prods = this.productData.slice(0, 8);
        
        if (this.charts.product) {
          this.charts.product.destroy();
          this.charts.product = null;
        }
        
        if (this.$refs.productChart) {
          try {
            const ctx = this.$refs.productChart.getContext('2d');
            this.charts.product = new Chart(ctx, {
              type: 'bar',
              data: {
                labels: prods.map(p => {
                  const name = p.product_name || 'Unknown';
                  return name.length > 15 ? name.substring(0, 15) + '...' : name;
                }),
                datasets: [{
                  label: 'Revenue ($)',
                  data: prods.map(p => parseFloat(p.revenue_in_range || p.total_sales || 0)),
                  backgroundColor: ['#4299E1', '#48BB78', '#ED8936', '#9F7AEA', '#F56565', '#38B2AC', '#ECC94B', '#FC8181'],
                  borderColor: ['#4299E1', '#48BB78', '#ED8936', '#9F7AEA', '#F56565', '#38B2AC', '#ECC94B', '#FC8181'],
                  borderWidth: 2,
                  borderRadius: 6,
                  maxBarThickness: 50
                }]
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      title: function(context) {
                        const index = context[0].dataIndex;
                        return prods[index].product_name || 'Unknown';
                      },
                      label: function(context) {
                        return this.formatCurrency(context.parsed.y);
                      }
                    }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: function(value) {
                        return '$' + value.toLocaleString();
                      }
                    },
                    grid: { color: 'rgba(0, 0, 0, 0.05)' }
                  },
                  x: { 
                    grid: { display: false },
                    ticks: {
                      maxRotation: 45,
                      minRotation: 30,
                      font: { size: 10 }
                    }
                  }
                },
                onClick: (event, elements) => {
                  if (elements.length > 0) {
                    const index = elements[0].index;
                    const product = this.productData[index];
                    if (product) {
                      this.openProductDetailModal(product);
                    }
                  }
                }
              }
            });
          } catch (error) {
            console.error('Error rendering product chart:', error);
          }
        }
      }
    },

    renderOrderTypeCharts() {
      const orderTypes = this.orderTypeData.breakdown || [];
      
      if (!orderTypes.length) {
        return;
      }

      // Order Type Pie Chart
      if (this.$refs.orderTypeChart) {
        if (this.charts.orderType) {
          this.charts.orderType.destroy();
          this.charts.orderType = null;
        }
        
        try {
          const ctx = this.$refs.orderTypeChart.getContext('2d');
          this.charts.orderType = new Chart(ctx, {
            type: 'doughnut',
            data: {
              labels: orderTypes.map(o => o.order_type || 'Unknown'),
              datasets: [{
                data: orderTypes.map(o => o.order_count || 0),
                backgroundColor: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#ec4899'],
                borderColor: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#ec4899'],
                borderWidth: 2
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'bottom',
                  labels: {
                    padding: 20,
                    usePointStyle: true,
                    pointStyle: 'circle'
                  }
                },
                tooltip: {
                  callbacks: {
                    label: function(context) {
                      const total = context.dataset.data.reduce((a, b) => a + b, 0);
                      const percentage = total > 0 ? ((context.parsed / total) * 100).toFixed(1) : 0;
                      return `${context.label}: ${context.parsed} orders (${percentage}%)`;
                    }
                  }
                }
              },
              cutout: '60%'
            }
          });
        } catch (error) {
          console.error('Error rendering order type pie chart:', error);
        }
      }

      // Order Type Units Chart
      if (this.$refs.orderTypeUnitsChart) {
        if (this.charts.orderTypeUnits) {
          this.charts.orderTypeUnits.destroy();
          this.charts.orderTypeUnits = null;
        }
        
        try {
          const ctx = this.$refs.orderTypeUnitsChart.getContext('2d');
          this.charts.orderTypeUnits = new Chart(ctx, {
            type: 'bar',
            data: {
              labels: orderTypes.map(o => o.order_type || 'Unknown'),
              datasets: [
                {
                  label: 'Units Sold',
                  data: orderTypes.map(o => o.units_sold || 0),
                  backgroundColor: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#ec4899'],
                  borderColor: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#ec4899'],
                  borderWidth: 2,
                  borderRadius: 6
                }
              ]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: function(context) {
                      return context.parsed.y + ' units';
                    }
                  }
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  grid: { color: 'rgba(0, 0, 0, 0.05)' }
                },
                x: { grid: { display: false } }
              }
            }
          });
        } catch (error) {
          console.error('Error rendering order type units chart:', error);
        }
      }
    },

    renderVendorCharts() {
      const vendors = this.vendorData.breakdown || [];
      
      if (!vendors.length) {
        return;
      }

      // Vendor Revenue Chart
      if (this.$refs.vendorRevenueChart) {
        if (this.charts.vendorRevenue) {
          this.charts.vendorRevenue.destroy();
          this.charts.vendorRevenue = null;
        }
        
        try {
          const sorted = [...vendors].sort((a, b) => (b.revenue || 0) - (a.revenue || 0)).slice(0, 10);
          const ctx = this.$refs.vendorRevenueChart.getContext('2d');
          this.charts.vendorRevenue = new Chart(ctx, {
            type: 'bar',
            data: {
              labels: sorted.map(v => {
                const name = v.vendor || 'Unknown';
                return name.length > 15 ? name.substring(0, 15) + '...' : name;
              }),
              datasets: [{
                label: 'Revenue ($)',
                data: sorted.map(v => v.revenue || 0),
                backgroundColor: '#6366f1',
                borderColor: '#6366f1',
                borderWidth: 2,
                borderRadius: 6
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: function(context) {
                      return '$' + context.parsed.y.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      });
                    }
                  }
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    callback: function(value) {
                      return '$' + value.toLocaleString();
                    }
                  },
                  grid: { color: 'rgba(0, 0, 0, 0.05)' }
                },
                x: { 
                  grid: { display: false },
                  ticks: {
                    maxRotation: 45,
                    minRotation: 30,
                    font: { size: 10 }
                  }
                }
              }
            }
          });
        } catch (error) {
          console.error('Error rendering vendor revenue chart:', error);
        }
      }

      // Vendor Returns Chart
      if (this.$refs.vendorReturnsChart) {
        if (this.charts.vendorReturns) {
          this.charts.vendorReturns.destroy();
          this.charts.vendorReturns = null;
        }
        
        try {
          const sorted = [...vendors].sort((a, b) => (b.return_rate || 0) - (a.return_rate || 0)).slice(0, 10);
          const ctx = this.$refs.vendorReturnsChart.getContext('2d');
          this.charts.vendorReturns = new Chart(ctx, {
            type: 'bar',
            data: {
              labels: sorted.map(v => {
                const name = v.vendor || 'Unknown';
                return name.length > 15 ? name.substring(0, 15) + '...' : name;
              }),
              datasets: [
                {
                  label: 'Returns',
                  data: sorted.map(v => v.returns || 0),
                  backgroundColor: '#ef4444',
                  borderColor: '#ef4444',
                  borderWidth: 2,
                  borderRadius: 6
                },
                {
                  label: 'Return Rate %',
                  data: sorted.map(v => v.return_rate || 0),
                  backgroundColor: '#f59e0b',
                  borderColor: '#f59e0b',
                  borderWidth: 2,
                  borderRadius: 6,
                  type: 'line'
                }
              ]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'top',
                  labels: {
                    padding: 10,
                    usePointStyle: true,
                    pointStyle: 'circle'
                  }
                },
                tooltip: {
                  callbacks: {
                    label: function(context) {
                      if (context.dataset.label === 'Returns') {
                        return `Returns: ${context.parsed.y}`;
                      }
                      return `Return Rate: ${context.parsed.y}%`;
                    }
                  }
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  grid: { color: 'rgba(0, 0, 0, 0.05)' }
                },
                x: { 
                  grid: { display: false },
                  ticks: {
                    maxRotation: 45,
                    minRotation: 30,
                    font: { size: 10 }
                  }
                }
              }
            }
          });
        } catch (error) {
          console.error('Error rendering vendor returns chart:', error);
        }
      }
    },

    renderStateCharts() {
      const states = this.stateData.breakdown || [];
      
      if (!states.length) {
        return;
      }

      // State Pie Chart
      if (this.$refs.statePieChart) {
        if (this.charts.statePie) {
          this.charts.statePie.destroy();
          this.charts.statePie = null;
        }
        
        try {
          const topStates = [...states].sort((a, b) => (b.item_count || 0) - (a.item_count || 0)).slice(0, 10);
          const colors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#8b5cf6', '#f97316', '#14b8a6', '#f43f5e'];
          
          const ctx = this.$refs.statePieChart.getContext('2d');
          this.charts.statePie = new Chart(ctx, {
            type: 'doughnut',
            data: {
              labels: topStates.map(s => s.state_text || 'Unknown'),
              datasets: [{
                data: topStates.map(s => s.item_count || 0),
                backgroundColor: colors.slice(0, topStates.length),
                borderColor: colors.slice(0, topStates.length),
                borderWidth: 2
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'bottom',
                  labels: {
                    padding: 20,
                    usePointStyle: true,
                    pointStyle: 'circle'
                  }
                },
                tooltip: {
                  callbacks: {
                    label: function(context) {
                      const total = context.dataset.data.reduce((a, b) => a + b, 0);
                      const percentage = total > 0 ? ((context.parsed / total) * 100).toFixed(1) : 0;
                      return `${context.label}: ${context.parsed} items (${percentage}%)`;
                    }
                  }
                }
              },
              cutout: '60%'
            }
          });
        } catch (error) {
          console.error('Error rendering state pie chart:', error);
        }
      }

      // State Bar Chart
      if (this.$refs.stateBarChart) {
        if (this.charts.stateBar) {
          this.charts.stateBar.destroy();
          this.charts.stateBar = null;
        }
        
        try {
          const topStates = [...states].sort((a, b) => (b.item_count || 0) - (a.item_count || 0)).slice(0, 10);
          const colors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#8b5cf6', '#f97316', '#14b8a6', '#f43f5e'];
          
          const ctx = this.$refs.stateBarChart.getContext('2d');
          this.charts.stateBar = new Chart(ctx, {
            type: 'bar',
            data: {
              labels: topStates.map(s => {
                const text = s.state_text || 'Unknown';
                return text.length > 15 ? text.substring(0, 15) + '...' : text;
              }),
              datasets: [{
                label: 'Items',
                data: topStates.map(s => s.item_count || 0),
                backgroundColor: colors.slice(0, topStates.length),
                borderColor: colors.slice(0, topStates.length),
                borderWidth: 2,
                borderRadius: 6
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: function(context) {
                      return context.parsed.y + ' items';
                    }
                  }
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  grid: { color: 'rgba(0, 0, 0, 0.05)' }
                },
                x: { 
                  grid: { display: false },
                  ticks: {
                    maxRotation: 45,
                    minRotation: 30,
                    font: { size: 10 }
                  }
                }
              }
            }
          });
        } catch (error) {
          console.error('Error rendering state bar chart:', error);
        }
      }
    },

    filterStateData() {
      if (this.stateViewFilter === 'all') {
        this.filteredStateData = this.stateData.breakdown || [];
        return;
      }
      
      const filterMap = {
        'delivered': [1],
        'returned': [-33, 33, 100, 101],
        'pending': [0, 2, 3, 4, 5, 6, 9, 10, 11, 12, 13, 14, 15, 30, 31],
        'cancelled': [32],
        'damaged': [-34]
      };
      
      const validStates = filterMap[this.stateViewFilter] || [];
      this.filteredStateData = (this.stateData.breakdown || []).filter(
        s => validStates.includes(s.state_value)
      );
    },

    renderCardCharts(key) {
      const period = this.periodCards.find(p => p.key === key);
      if (!period) {
        console.warn('Period not found for key:', key);
        return;
      }
      
      console.log('Rendering card charts for period:', key);
      this.renderCategoryPieChart(key, period);
      this.renderProductPieChart(key, period);
    },

    renderCategoryPieChart(key, period) {
      const canvas = document.getElementById('category_pie_' + key);
      if (!canvas) {
        console.warn('Category pie canvas not found for:', key);
        return;
      }
      
      if (this.statsPieCharts[key]) {
        this.statsPieCharts[key].destroy();
        delete this.statsPieCharts[key];
      }
      
      const data = period.categories || [];
      
      if (!data.length) {
        try {
          const ctx = canvas.getContext('2d');
          this.statsPieCharts[key] = new Chart(ctx, {
            type: 'doughnut',
            data: {
              labels: ['No Data'],
              datasets: [{ data: [1], backgroundColor: ['#e2e8f0'], borderWidth: 0 }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: false }, tooltip: { enabled: false } },
              cutout: '60%'
            }
          });
        } catch (error) {
          console.error('Error creating empty category pie chart:', error);
        }
        return;
      }
      
      try {
        const sortedData = [...data].sort((a, b) => b.total_revenue - a.total_revenue);
        const topData = sortedData.slice(0, 6);
        
        const ctx = canvas.getContext('2d');
        this.statsPieCharts[key] = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: topData.map(d => d.category_name || 'Unknown'),
            datasets: [{
              data: topData.map(d => parseFloat(d.total_revenue) || 0),
              backgroundColor: this.categoryColors.slice(0, topData.length).map(c => c + 'CC'),
              borderColor: this.categoryColors.slice(0, topData.length),
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { 
                display: true,
                position: 'bottom',
                labels: {
                  boxWidth: 10,
                  padding: 4,
                  font: { size: 8 }
                }
              },
              tooltip: {
                backgroundColor: 'rgba(15, 23, 42, 0.92)',
                titleFont: { size: 10, weight: '600' },
                bodyFont: { size: 9 },
                padding: 6,
                cornerRadius: 4,
                callbacks: {
                  label: (ctx) => {
                    const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                    const percentage = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
                    return `${ctx.label}: ${this.formatCurrency(ctx.parsed)} (${percentage}%)`;
                  }
                }
              }
            },
            cutout: '55%',
            animation: { animateRotate: true, duration: 600 }
          }
        });
      } catch (error) {
        console.error('Error rendering category pie chart:', error);
      }
    },

    renderProductPieChart(key, period) {
      const canvas = document.getElementById('product_pie_' + key);
      if (!canvas) {
        console.warn('Product pie canvas not found for:', key);
        return;
      }
      
      if (this.paymentPieCharts[key]) {
        this.paymentPieCharts[key].destroy();
        delete this.paymentPieCharts[key];
      }
      
      const data = period.topProducts || [];
      
      if (!data.length) {
        try {
          const ctx = canvas.getContext('2d');
          this.paymentPieCharts[key] = new Chart(ctx, {
            type: 'doughnut',
            data: {
              labels: ['No Data'],
              datasets: [{ data: [1], backgroundColor: ['#e2e8f0'], borderWidth: 0 }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: false }, tooltip: { enabled: false } },
              cutout: '60%'
            }
          });
        } catch (error) {
          console.error('Error creating empty product pie chart:', error);
        }
        return;
      }
      
      try {
        const sortedData = [...data].sort((a, b) => b.units - a.units);
        const topData = sortedData.slice(0, 6);
        
        const ctx = canvas.getContext('2d');
        this.paymentPieCharts[key] = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: topData.map(d => {
              const name = d.name || 'Unknown';
              return name.length > 15 ? name.substring(0, 15) + '...' : name;
            }),
            datasets: [{
              data: topData.map(d => parseFloat(d.units) || 0),
              backgroundColor: this.productColors.slice(0, topData.length).map(c => c + 'CC'),
              borderColor: this.productColors.slice(0, topData.length),
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { 
                display: true,
                position: 'bottom',
                labels: {
                  boxWidth: 10,
                  padding: 4,
                  font: { size: 8 }
                }
              },
              tooltip: {
                backgroundColor: 'rgba(15, 23, 42, 0.92)',
                titleFont: { size: 10, weight: '600' },
                bodyFont: { size: 9 },
                padding: 6,
                cornerRadius: 4,
                callbacks: {
                  label: (ctx) => {
                    const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                    const percentage = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
                    return `${ctx.label}: ${ctx.parsed} units (${percentage}%)`;
                  }
                }
              }
            },
            cutout: '50%',
            animation: { animateRotate: true, duration: 600 }
          }
        });
      } catch (error) {
        console.error('Error rendering product pie chart:', error);
      }
    },

    // ===== CATEGORY LIST =====
    openCategoryList() {
      this.isModalOpen = true;
      this.showCategoryListModal = true;
    },

    // ===== CATEGORY DETAIL =====
    async openCategoryDetail(category) {
      if (!category) return;
      this.categoryDetailTitle = `${category.category_name || 'Category'} - Category Details`;
      this.showCategoryDetailModal = true;
      this.isModalOpen = true;
      this.isCategoryDetailLoading = true;
      
      try {
        // Use existing product data - NO API CALLS NEEDED
        const productsInCategory = this.productData 
          ? this.productData.filter(p => p.category_id === category.category_id) 
          : [];
        
        // Build location and salesperson data from the products you already have
        const locationMap = {};
        const salespersonMap = {};
        
        // Use the product data you already have
        productsInCategory.forEach(product => {
          // You already have location and salesperson data in the product
          const locationId = product.location_id;
          if (locationId) {
            const locName = this.getLocationName(locationId);
            if (!locationMap[locationId]) {
              locationMap[locationId] = {
                id: locationId,
                name: locName,
                productCount: 0,
                revenue: 0,
                unitsSold: 0,
                orders: 0
              };
            }
            locationMap[locationId].productCount += 1;
            locationMap[locationId].revenue += (product.revenue_in_range || product.total_sales || 0);
            locationMap[locationId].unitsSold += (product.sold_in_range || product.total_units_sold || 0);
            locationMap[locationId].orders += product.order_count || 0;
          }
          
          const salespersonName = product.salesperson_name;
          if (salespersonName) {
            if (!salespersonMap[salespersonName]) {
              salespersonMap[salespersonName] = {
                name: salespersonName,
                productCount: 0,
                revenue: 0,
                unitsSold: 0,
                orders: 0,
                commission: 0
              };
            }
            salespersonMap[salespersonName].productCount += 1;
            salespersonMap[salespersonName].revenue += (product.revenue_in_range || product.total_sales || 0);
            salespersonMap[salespersonName].unitsSold += (product.sold_in_range || product.total_units_sold || 0);
            salespersonMap[salespersonName].orders += product.order_count || 0;
          }
        });
        
        this.categoryDetailData = {
          categoryId: category.category_id,
          categoryName: category.category_name,
          productCount: productsInCategory.length,
          revenue: category.total_sales || 0,
          unitsSold: category.total_units_sold || 0,
          orders: category.order_count || 0,
          avgProfit: category.avg_profit_margin || 0,
          products: productsInCategory.map(p => ({
            ...p,
            sold_in_range: p.sold_in_range || p.total_units_sold || 0,
            revenue_in_range: p.revenue_in_range || p.total_sales || 0,
            average_profit_margin: p.average_profit_margin || 0,
            avg_selling_price: p.avg_selling_price || (p.revenue_in_range / p.sold_in_range) || 0
          })),
          locations: Object.values(locationMap).sort((a, b) => b.revenue - a.revenue),
          salespeople: Object.values(salespersonMap).sort((a, b) => b.revenue - a.revenue)
        };
        
      } catch (error) {
        console.error('Error preparing category details:', error);
        this.$bvToast.toast('Error loading category details', { title: 'Error', variant: 'danger' });
      } finally {
        this.isCategoryDetailLoading = false;
      }
    },

    // ===== PRODUCT DETAIL =====
    async openProductDetailModal(product) {
      if (!product) return;
      this.productModalTitle = `${product.product_name || 'Product'} - Product Details`;
      this.showProductDetailModal = true;
      this.isModalOpen = true;
      this.isProductDetailLoading = true;
      
      try {
        const username = this.userDetails.username || this.userDetails.name || '';
        const response = await this.makeRequest(
          '/api/product/get_product_performance/', 
          {
            view_type: 'product_detail',
            product_sku: product.sku,
            username: username,
            start_date: this.dateRange[0],
            end_date: this.dateRange[1]
          },
          { cancelToken: this.cancelTokenSource.token }
        );
        
        if (response && response.result) {
          const transactions = response.result.transactions || [];
          const productInfo = response.result.product || product;
          
          const locationMap = {};
          const salespersonMap = {};
          
          transactions.forEach(tx => {
            const locId = tx.store_location_id;
            if (locId) {
              const locName = this.getLocationName(locId);
              if (!locationMap[locId]) {
                locationMap[locId] = {
                  id: locId,
                  name: locName,
                  unitsSold: 0,
                  revenue: 0,
                  orders: 0
                };
              }
              locationMap[locId].unitsSold += tx.quantity || 0;
              locationMap[locId].revenue += (tx.sold_price || 0) * (tx.quantity || 0);
              locationMap[locId].orders += 1;
            }
            
            const spName = tx.salesperson_name;
            if (spName) {
              if (!salespersonMap[spName]) {
                salespersonMap[spName] = {
                  name: spName,
                  unitsSold: 0,
                  revenue: 0,
                  commission: 0
                };
              }
              salespersonMap[spName].unitsSold += tx.quantity || 0;
              salespersonMap[spName].revenue += (tx.sold_price || 0) * (tx.quantity || 0);
            }
          });
          
          this.productDetailData = {
            ...productInfo,
            locations: Object.values(locationMap).sort((a, b) => b.revenue - a.revenue),
            salespeople: Object.values(salespersonMap).sort((a, b) => b.revenue - a.revenue),
            transactions: transactions
          };
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('Product detail request cancelled');
          return;
        }
        console.error('Error fetching product details:', error);
        this.$bvToast.toast('Error loading product details', { title: 'Error', variant: 'danger' });
      } finally {
        this.isProductDetailLoading = false;
      }
    },

    // ===== VENDOR DETAIL =====
    async openVendorDetail(vendor) {
      if (!vendor) return;
      this.vendorDetailTitle = `${vendor.vendor || 'Vendor'} - Vendor Details`;
      this.showVendorDetailModal = true;
      this.isModalOpen = true;
      this.isVendorDetailLoading = true;
      
      try {
        const username = this.userDetails.username || this.userDetails.name || '';
        const response = await this.makeRequest(
          '/api/product/get_vendor_detail/', 
          {
            vendor: vendor.vendor,
            username: username,
            start_date: this.dateRange[0],
            end_date: this.dateRange[1]
          },
          { cancelToken: this.cancelTokenSource.token }
        );
        
        if (response && response.result) {
          this.vendorDetailData = response.result;
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('Vendor detail request cancelled');
          return;
        }
        console.error('Error fetching vendor details:', error);
        this.$bvToast.toast('Error loading vendor details', { title: 'Error', variant: 'danger' });
      } finally {
        this.isVendorDetailLoading = false;
      }
    },

    openPeriodModal(key) {
      const period = this.periodCards.find(p => p.key === key);
      if (period) {
        this.activePeriod = key;
        this.$bvToast.toast(`Showing detailed data for ${period.label}`, {
          title: 'Period Selected',
          variant: 'info',
          autoHideDelay: 2000
        });
      }
    },

    openProductModal(product) {
      if (product) {
        this.openProductDetailModal(product);
      } else {
        this.$bvToast.toast('Product list view coming soon', {
          title: 'Info',
          variant: 'info'
        });
      }
    }
  }
};
</script>

<style scoped>
/* ===== RESET & BASE ===== */
.product-performance-dashboard {
  padding: 16px;
  background: #f1f5f9;
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ===== 3D CUBE LOADER ===== */
.global-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  background: #f1f5f9;
}

.loader-3d {
  width: 70px;
  height: 70px;
  perspective: 600px;
}

.loader-3d.small {
  width: 40px;
  height: 40px;
}

.cube {
  width: 100%;
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  animation: rotateCube 2s infinite ease-in-out;
}

@keyframes rotateCube {
  0% { transform: rotateX(-30deg) rotateY(0deg); }
  50% { transform: rotateX(-30deg) rotateY(180deg); }
  100% { transform: rotateX(-30deg) rotateY(360deg); }
}

.cube .face {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 2px solid rgba(99, 102, 241, 0.3);
  border-radius: 6px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.8), rgba(139, 92, 246, 0.8));
  box-shadow: 0 0 30px rgba(99, 102, 241, 0.2);
}

.cube .face.front { transform: translateZ(calc(100% / 2)); }
.cube .face.back { transform: rotateY(180deg) translateZ(calc(100% / 2)); }
.cube .face.right { transform: rotateY(90deg) translateZ(calc(100% / 2)); }
.cube .face.left { transform: rotateY(-90deg) translateZ(calc(100% / 2)); }
.cube .face.top { transform: rotateX(90deg) translateZ(calc(100% / 2)); }
.cube .face.bottom { transform: rotateX(-90deg) translateZ(calc(100% / 2)); }

.loader-text {
  margin-top: 20px;
  color: #64748b;
  font-size: 14px;
  animation: pulseText 1.5s ease-in-out infinite;
}

@keyframes pulseText {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

/* ===== TAB LOADER ===== */
.tab-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 40px 20px;
  background: white;
  border-radius: 12px;
}

.tab-loader .loader-text {
  margin-top: 16px;
  color: #64748b;
  font-size: 14px;
}

/* ===== HEADER ===== */
.dashboard-header {
  background: linear-gradient(135deg, #0f172a, #1e293b);
  border-radius: 16px;
  padding: 20px 28px;
  margin-bottom: 16px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.15);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  width: 44px;
  height: 44px;
  background: rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #818cf8;
}

.header-title {
  font-size: 20px;
  font-weight: 700;
  color: white;
  margin: 0;
  letter-spacing: -0.5px;
}

.header-subtitle {
  font-size: 13px;
  color: rgba(255,255,255,0.6);
  margin: 0;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.badge-dot {
  width: 8px;
  height: 8px;
  background: #34d399;
  border-radius: 50%;
  animation: pulse-dot 2s infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.header-user {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255,255,255,0.8);
  font-size: 13px;
}

/* ===== FILTER BAR ===== */
.filter-bar {
  background: white;
  border-radius: 12px;
  padding: 12px 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.filter-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.filter-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.filter-label {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}

.filter-right {
  display: flex;
  align-items: center;
}

.date-picker {
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

/* ===== MAIN TABS ===== */
.main-tabs >>> .nav-tabs .nav-link {
  border: none;
  color: #64748b;
  font-weight: 500;
  padding: 10px 20px;
  border-radius: 10px;
  transition: all 0.3s ease;
}

.main-tabs >>> .nav-tabs .nav-link:hover {
  background: rgba(99, 102, 241, 0.05);
  color: #1e293b;
}

.main-tabs >>> .nav-tabs .nav-link.active {
  background: #6366f1;
  color: white;
  box-shadow: 0 4px 15px rgba(99, 102, 241, 0.3);
}

.main-tabs >>> .tab-content {
  padding: 20px 0 0;
}

/* ===== FLIP CARDS ===== */
.period-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.flip-card {
  perspective: 1000px;
  height: 300px;
}

.flip-card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  transform-style: preserve-3d;
}

.flip-card.flipped .flip-card-inner {
  transform: rotateY(180deg);
}

.flip-card-front,
.flip-card-back {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 16px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  border: 2px solid transparent;
  transition: border-color 0.3s;
  cursor: pointer;
}

.flip-card-front {
  background: white;
  transform: rotateY(0deg);
}

.flip-card-back {
  background: white;
  transform: rotateY(180deg);
}

.flip-card.active .flip-card-front,
.flip-card.active .flip-card-back {
  border-color: #6366f1;
  background: linear-gradient(135deg, #f8faff, #eef2ff);
}

.flip-card-front:hover,
.flip-card-back:hover {
  box-shadow: 0 8px 30px rgba(0,0,0,0.1);
}

.flip-card-header {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 6px;
}

.flip-label {
  font-size: 18px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  font-weight: 700;
}

.flip-change-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 3px 12px;
  margin: 0 0 6px 0;
  border-radius: 20px;
  background: #f8fafc;
}

.change-badge {
  font-size: 12px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 12px;
}

.change-badge.up {
  color: #10b981;
  background: rgba(16, 185, 129, 0.1);
}

.change-badge.down {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

.change-badge.flat {
  color: #64748b;
  background: rgba(100, 116, 139, 0.1);
}

.change-label {
  font-size: 10px;
  color: #94a3b8;
}

.flip-main-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 8px;
}

.flip-stat {
  background: #f8fafc;
  border-radius: 10px;
  padding: 6px 10px;
  text-align: center;
  border: 1px solid #eef2ff;
}

.flip-stat-label {
  display: block;
  font-size: 9px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-weight: 600;
}

.flip-stat-value {
  display: block;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.2;
  margin-top: 2px;
}

.flip-stat-value.primary { color: #2c2c2c; }
.flip-stat-value.success { color: #10b981; }

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  display: inline-block;
}

.flip-detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 5px;
  margin-bottom: 6px;
  flex: 1;
}

.flip-detail-card {
  text-align: center;
  background: #f8fafc;
  border-radius: 6px;
  padding: 4px 8px;
  display: flex;
  flex-direction: column;
  border: 1px solid #f1f5f9;
}

.flip-detail-label {
  font-size: 15px;
  color: #94a3b8;
  font-weight: 500;
}

.flip-detail-value {
  font-size: 17px;
  font-weight: 700;
  color: #1e293b;
}

.flip-detail-value.text-danger {
  color: #ef4444;
}

.flip-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 6px;
  border-top: 1px solid #f1f5f9;
}

.flip-orders {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
}

.flip-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.flip-hint {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #6366f1;
  font-weight: 500;
  background: rgba(99, 102, 241, 0.08);
  padding: 3px 10px;
  border-radius: 16px;
  transition: all 0.3s;
  cursor: pointer;
}

.flip-hint.back {
  background: rgba(16, 185, 129, 0.08);
  color: #10b981;
}

.flip-hint:hover {
  background: rgba(99, 102, 241, 0.15);
  transform: scale(1.05);
}

.flip-hint.back:hover {
  background: rgba(16, 185, 129, 0.15);
}

.flip-hint-icon {
  font-size: 12px;
  animation: spinHint 3s linear infinite;
}

@keyframes spinHint {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* BACK SIDE */
.flip-back-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.flip-back-label {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}

.flip-charts-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  flex: 1;
  min-height: 0;
}

.flip-chart-section {
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  border-radius: 10px;
  padding: 6px 8px;
  min-height: 0;
}

.flip-chart-title {
  font-size: 9px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  text-align: center;
  margin-bottom: 3px;
}

.flip-chart-wrapper {
  flex: 1;
  min-height: 0;
  position: relative;
}

.flip-chart {
  width: 100% !important;
  height: 100% !important;
  max-height: 110px;
}

.flip-chart-stats {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-top: 3px;
  padding-top: 3px;
  border-top: 1px solid #e2e8f0;
}

.flip-chart-stats.payment-stats {
  max-height: 50px;
  overflow-y: auto;
}

.stats-label-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 8px;
  color: #1e293b;
  padding: 1px 0;
}

.stats-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

.stats-label-text {
  flex: 1;
  font-weight: 500;
  font-size: 10px;
  color: #475569;
}

.stats-label-value {
  font-weight: 600;
  color: #1e293b;
  font-size: 10px;
}

.flip-back-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 6px;
  border-top: 1px solid #f1f5f9;
  margin-top: 4px;
}

.flip-payment-count {
  font-size: 10px;
  color: #94a3b8;
}

/* ===== CHARTS ROW ===== */
.charts-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.chart-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border: 1px solid #e2e8f0;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.chart-header h6 {
  margin: 0;
  font-weight: 600;
  color: #1e293b;
  font-size: 14px;
}

.chart-container {
  height: 200px;
  position: relative;
}

/* ===== PRODUCT STATS ROW ===== */
.product-stats-row,
.order-type-stats-row,
.vendor-stats-row,
.state-stats-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.stats-card {
  background: white;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border: 1px solid #e2e8f0;
}

.stats-card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: white;
  flex-shrink: 0;
}

.stats-card-icon.total-products { background: linear-gradient(135deg, #6366f1, #8b5cf6); }
.stats-card-icon.total-revenue { background: linear-gradient(135deg, #10b981, #34d399); }
.stats-card-icon.total-units { background: linear-gradient(135deg, #f59e0b, #fbbf24); }
.stats-card-icon.avg-profit { background: linear-gradient(135deg, #ef4444, #f87171); }
.stats-card-icon.order-types { background: linear-gradient(135deg, #6366f1, #8b5cf6); }
.stats-card-icon.store-pickup { background: linear-gradient(135deg, #10b981, #34d399); }
.stats-card-icon.home-delivery { background: linear-gradient(135deg, #f59e0b, #fbbf24); }
.stats-card-icon.layaway { background: linear-gradient(135deg, #ec4899, #f472b6); }
.stats-card-icon.regular { background: linear-gradient(135deg, #06b6d4, #22d3ee); }
.stats-card-icon.total-vendors { background: linear-gradient(135deg, #6366f1, #8b5cf6); }
.stats-card-icon.total-sales { background: linear-gradient(135deg, #10b981, #34d399); }
.stats-card-icon.total-returns { background: linear-gradient(135deg, #ef4444, #f87171); }
.stats-card-icon.top-vendor { background: linear-gradient(135deg, #f59e0b, #fbbf24); }
.stats-card-icon.total-items { background: linear-gradient(135deg, #6366f1, #8b5cf6); }
.stats-card-icon.delivered { background: linear-gradient(135deg, #10b981, #34d399); }
.stats-card-icon.returned { background: linear-gradient(135deg, #ef4444, #f87171); }
.stats-card-icon.pending { background: linear-gradient(135deg, #f59e0b, #fbbf24); }
.stats-card-icon.cancelled { background: linear-gradient(135deg, #64748b, #94a3b8); }

.stats-card-content {
  flex: 1;
}

.stats-card-value {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}

.stats-card-value.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
}

.stats-card-label {
  display: block;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

/* ===== PAYMENT SECTION ===== */
.payment-section {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border: 1px solid #e2e8f0;
}

.payment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.payment-methods-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.payment-method-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.payment-method-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(66, 153, 225, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #4299e1;
  flex-shrink: 0;
}

.payment-method-info {
  flex: 1;
  min-width: 0;
}

.payment-method-name {
  font-size: 0.8rem;
  font-weight: 600;
  color: #2d3748;
}

.payment-method-amount {
  font-size: 0.9rem;
  font-weight: 700;
  color: #2d3748;
}

.payment-method-bar {
  height: 4px;
  background: #edf2f7;
  border-radius: 2px;
  margin-top: 4px;
  overflow: hidden;
}

.payment-method-fill {
  height: 100%;
  background: #4299e1;
  border-radius: 2px;
  transition: width 0.6s ease;
}

/* ===== STATE FILTER GROUP ===== */
.state-filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ===== MODAL STYLES ===== */
.period-modal-header {
  background: linear-gradient(135deg, #0f172a, #1e293b);
  color: white;
  border-bottom: none;
  padding: 14px 24px;
}

.period-modal-header .close {
  color: white;
  opacity: 0.8;
}

.period-modal-header .close:hover {
  opacity: 1;
  transform: rotate(90deg);
}

.period-modal-body {
  padding: 18px 22px;
  background: #f8fafc;
}

.modal-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.modal-summary-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  margin-bottom: 16px;
}

.modal-summary-item {
  background: white;
  padding: 12px 14px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.modal-summary-item .summary-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  flex-shrink: 0;
}

.summary-icon.total { background: linear-gradient(135deg, #6366f1, #8b5cf6); }
.summary-icon.revenue { background: linear-gradient(135deg, #10b981, #34d399); }
.summary-icon.units { background: linear-gradient(135deg, #f59e0b, #fbbf24); }
.summary-icon.orders { background: linear-gradient(135deg, #06b6d4, #22d3ee); }
.summary-icon.profit { background: linear-gradient(135deg, #ef4444, #f87171); }
.summary-icon.returns { background: linear-gradient(135deg, #ef4444, #f87171); }

.modal-summary-item .label {
  display: block;
  font-size: 9px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.modal-summary-item .value {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}

.modal-summary-item .value.text-danger { color: #ef4444; }

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin: 16px 0 10px;
  padding-bottom: 6px;
  border-bottom: 2px solid #e2e8f0;
}

.modal-tabs >>> .nav-tabs .nav-link {
  border: none;
  color: #64748b;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 8px;
}

.modal-tabs >>> .nav-tabs .nav-link.active {
  background: #6366f1;
  color: white;
}

.modal-tabs >>> .tab-content {
  padding: 16px 0 0;
}

.modal-table {
  width: 100%;
  font-size: 12px;
  border-collapse: collapse;
}

.modal-table th,
.modal-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
}

.modal-table th {
  background: #f1f5f9;
  font-weight: 600;
  color: #475569;
  position: sticky;
  top: 0;
  z-index: 1;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.modal-table tbody tr:hover {
  background: #f1f5f9;
}

.modal-table .table-total {
  background: #f1f5f9;
  font-weight: 700;
}

.modal-table .table-total td {
  border-top: 2px solid #e2e8f0;
}

.table-responsive-wrapper {
  overflow-x: auto;
  max-height: 350px;
  overflow-y: auto;
  border-radius: 10px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 12px 16px;
  border-top: 1px solid #e2e8f0;
}

/* ===== PRODUCT DETAIL HEADER ===== */
.product-detail-header {
  display: flex;
  gap: 24px;
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  border: 1px solid #e2e8f0;
}

.product-detail-image-wrapper {
  flex-shrink: 0;
}

.product-detail-image {
  width: 150px;
  height: 150px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}

.product-detail-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-detail-image-placeholder {
  width: 150px;
  height: 150px;
  border-radius: 12px;
  border: 2px dashed #cbd5e0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
}

.product-detail-info {
  flex: 1;
}

.product-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}

.product-stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.stat-item {
  background: #f8fafc;
  padding: 8px 12px;
  border-radius: 8px;
  text-align: center;
}

.stat-label {
  display: block;
  font-size: 0.7rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.stat-value {
  display: block;
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
}

/* ===== PRODUCT CELL ===== */
.product-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.product-thumb {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.product-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-name {
  font-weight: 500;
  color: #1e293b;
}

.invoice-link {
  color: #4299e1;
  font-weight: 500;
  text-decoration: none;
}

.invoice-link:hover {
  color: #3182ce;
  text-decoration: underline;
}

.clickable-rows tbody tr {
  cursor: pointer;
}

.clickable-rows tbody tr:hover {
  background: #eef2ff;
}

/* ===== ORDER TYPES DASHBOARD ===== */
.order-types-dashboard,
.vendors-dashboard,
.state-analysis-dashboard {
  padding: 4px 0;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 1200px) {
  .period-grid { grid-template-columns: repeat(2, 1fr); }
  .modal-summary-grid { grid-template-columns: repeat(3, 1fr); }
  .product-stats-row,
  .order-type-stats-row,
  .vendor-stats-row,
  .state-stats-row { grid-template-columns: repeat(3, 1fr); }
}

@media (max-width: 992px) {
  .charts-row { grid-template-columns: 1fr; }
  .product-stats-row,
  .order-type-stats-row,
  .vendor-stats-row,
  .state-stats-row { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 768px) {
  .period-grid { grid-template-columns: 1fr; }
  .flip-card { height: 420px; }
  .modal-summary-grid { grid-template-columns: repeat(2, 1fr); }
  .payment-methods-grid { grid-template-columns: 1fr 1fr; }
  .filter-content { flex-direction: column; align-items: stretch; }
  .filter-left { flex-wrap: wrap; }
  .header-content { flex-direction: column; gap: 12px; align-items: flex-start; }
  .header-right { width: 100%; justify-content: flex-start; }
  .flip-detail-grid { grid-template-columns: 1fr 1fr; }
  .flip-charts-layout { grid-template-columns: 1fr; }
  .flip-chart-wrapper { min-height: 60px; }
  .product-stats-row,
  .order-type-stats-row,
  .vendor-stats-row,
  .state-stats-row { grid-template-columns: 1fr 1fr; }
  .product-detail-header { flex-direction: column; align-items: center; text-align: center; }
  .product-stats-grid { grid-template-columns: repeat(2, 1fr); }
  .product-detail-image { width: 120px; height: 120px; }
  .product-detail-image-placeholder { width: 120px; height: 120px; }
}

@media (max-width: 576px) {
  .period-grid { grid-template-columns: 1fr; }
  .modal-summary-grid { grid-template-columns: 1fr; }
  .payment-methods-grid { grid-template-columns: 1fr; }
  .flip-card { height: 460px; }
  .flip-detail-grid { grid-template-columns: 1fr; }
  .flip-stat-value { font-size: 20px; }
  .flip-detail-value { font-size: 13px; }
  .chart-container { height: 160px; }
  .product-stats-row,
  .order-type-stats-row,
  .vendor-stats-row,
  .state-stats-row { grid-template-columns: 1fr; }
  .product-stats-grid { grid-template-columns: 1fr 1fr; }
  .main-tabs >>> .nav-tabs .nav-link { padding: 6px 12px; font-size: 12px; }
}
</style>


// WEBPACK FOOTER //
// src/components/ProductReports/ProductReportsDashboard.vue